/**
 * API Client — JWT-authenticated, with auto-refresh and tenant isolation.
 */

import { createLogger } from "../utils/logger";
const log = createLogger("API");

const API_BASE = import.meta.env.VITE_API_URL || "";

/**
 * Turn an error body's `error` / `detail` into a sentence a user can read.
 *
 * A structured detail — `{message, error_type, ...}`, as returned by
 * /api/files/upload's parse errors — carries its human sentence in `message`.
 * Without that branch the whole object fell through to JSON.stringify and the
 * user was shown raw JSON on screen.
 */
export function errorMessageFrom(raw) {
  if (typeof raw === "string") return raw;
  if (Array.isArray(raw)) return raw.map((e) => e.msg || JSON.stringify(e)).join("; ");
  if (raw && typeof raw.message === "string") return raw.message;
  return JSON.stringify(raw);
}

// ─── Session storage (F / #16) ───────────────────────────
//
// The JWT no longer lives in localStorage — it rides in HttpOnly cookies
// (i2_access / i2_refresh) the browser sends automatically and JS cannot read,
// so an XSS payload can't exfiltrate it. Only NON-sensitive profile data
// (user, tenant) is cached in localStorage for instant first paint. A readable
// CSRF cookie (i2_csrf) is echoed as X-CSRF-Token on state-changing requests.

const TOKEN_KEY = "agentic_access_token";   // legacy — cleared, never written
const REFRESH_KEY = "agentic_refresh_token"; // legacy — cleared, never written
const USER_KEY = "agentic_user";
const TENANT_KEY = "agentic_tenant";

const CSRF_COOKIE = "i2_csrf";

/** Read a cookie value by name (only non-HttpOnly cookies are visible). */
function readCookie(name) {
  if (typeof document === "undefined" || !document.cookie) return null;
  const m = document.cookie.match(new RegExp("(?:^|; )" + name.replace(/([.$?*|{}()[\]\\/+^])/g, "\\$1") + "=([^;]*)"));
  return m ? decodeURIComponent(m[1]) : null;
}

/** The current double-submit CSRF token (from the readable i2_csrf cookie). */
export function getCsrfToken() { return readCookie(CSRF_COOKIE); }

// Legacy getters retained so existing importers don't break; with cookie
// sessions there is no JS-visible access/refresh token, so these are null.
export function getAccessToken() { return localStorage.getItem(TOKEN_KEY); }
export function getRefreshToken() { return localStorage.getItem(REFRESH_KEY); }

export function getStoredUser() {
  try { return JSON.parse(localStorage.getItem(USER_KEY)); } catch { return null; }
}
export function getStoredTenant() {
  try { return JSON.parse(localStorage.getItem(TENANT_KEY)); } catch { return null; }
}
export function setStoredTenant(tenant) {
  localStorage.setItem(TENANT_KEY, JSON.stringify(tenant));
}

function storeAuth(data) {
  // F: tokens are delivered as HttpOnly cookies by the server — never stored
  // in JS. Only cache the non-sensitive profile for first paint.
  if (data.user) localStorage.setItem(USER_KEY, JSON.stringify(data.user));
  if (data.tenant) localStorage.setItem(TENANT_KEY, JSON.stringify(data.tenant));
}

export function clearAuth() {
  // Drop any legacy localStorage tokens from a pre-cookie session too.
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem(TENANT_KEY);
}

// A live cookie session is present iff the readable CSRF cookie exists (it's
// set alongside the HttpOnly access cookie on login and cleared on logout).
export function isAuthenticated() {
  return !!getCsrfToken();
}

// ─── Core Request ────────────────────────────────────────

// Map a non-JSON error response (a 502/503/504 HTML page from the load
// balancer, an nginx timeout, or any other body that isn't JSON) to a
// readable, status-aware message — instead of surfacing the raw
// "Unexpected token '<', "<html>…" is not valid JSON" parse error.
// Pure + exported so each branch is a single-line statement with stable
// v8 line-coverage attribution (a nested multi-line ternary flakes in the
// merged lcov) and is unit-testable directly.
/**
 * A1-CAPTCHA: read the Turnstile challenge signal off a thrown login error.
 * After N failed logins the backend returns 401 with
 * ``detail: {captcha_required, captcha_sitekey}`` (surfaced on ``err.body``).
 * Pure + unit-testable so the login component just renders the widget on
 * ``required``.
 */
export function captchaFromError(err) {
  const d = err && err.body && err.body.detail;
  if (d && typeof d === "object" && d.captcha_required) {
    return { required: true, sitekey: d.captcha_sitekey || "" };
  }
  return { required: false, sitekey: "" };
}

export function nonJsonErrorMessage(status) {
  if (status === 502 || status === 503 || status === 504) {
    return "The service is temporarily unavailable or timed out. If it's an on-demand model, it may still be warming up — wait a moment and try again.";
  }
  if (status >= 500) {
    return "The server hit an unexpected error. Please try again.";
  }
  return `The server returned an unexpected response (HTTP ${status || "?"}). Please try again.`;
}

const _UNSAFE_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);

/** Add the double-submit CSRF header on state-changing requests (F/#16). */
function withCsrf(headers, method) {
  const m = (method || "GET").toUpperCase();
  if (_UNSAFE_METHODS.has(m)) {
    const csrf = getCsrfToken();
    if (csrf) headers["X-CSRF-Token"] = csrf;
  }
  return headers;
}

export async function request(path, options = {}, retry = true) {
  const url = `${API_BASE}${path}`;
  // Strip out non-fetch options so they don't get passed to fetch().
  // quietStatuses: statuses the caller expects + handles (e.g. a 412 "capability
  // not applicable" read) — don't log them as errors. Destructured out so they
  // never reach fetch().
  // inlineConfigRequired: the caller shows its own prompt for a 412
  // configuration_required (e.g. the Add-model wizard's token field), so don't
  // also raise the global "Configure now" banner for it.
  const { rawResponse, quietStatuses, inlineConfigRequired, ...fetchOptions } = options;
  // For FormData bodies, let the browser set the multipart Content-Type
  // (with boundary). Only default to JSON for non-FormData bodies.
  const isFormData = typeof FormData !== "undefined" && fetchOptions.body instanceof FormData;
  const headers = withCsrf({
    ...(isFormData ? {} : { "Content-Type": "application/json" }),
    ...fetchOptions.headers,
  }, fetchOptions.method);
  // F (#16): auth rides in HttpOnly cookies now — send them on every call
  // (same-origin) instead of an Authorization: Bearer header from localStorage.
  try {
    const res = await fetch(url, { ...fetchOptions, headers, credentials: "include" });

    // Auto-refresh on 401
    if (res.status === 401 && retry) {
      const refreshed = await tryRefreshToken();
      if (refreshed) return request(path, options, false);
      clearAuth();
      window.dispatchEvent(new Event("auth:logout"));
      throw new Error("Session expired");
    }

    // rawResponse: caller wants the Response object itself (for file
    // downloads, streaming, non-JSON payloads). Only return on success
    // so the standard error-handling path below still runs on non-2xx.
    if (rawResponse && res.ok) {
      return res;
    }

    // Parse the body as JSON, but tolerate a non-JSON response — a 502/503/504
    // from the load balancer (or an nginx timeout) is an HTML page, not JSON.
    // Turn those into a readable, status-aware message instead of surfacing the
    // raw "Unexpected token '<', "<html>…" is not valid JSON" parse error.
    let data;
    try {
      data = await res.json();
    } catch {
      if (res.ok) return {};   // 2xx with an empty/non-JSON body — nothing to parse
      const err = new Error(nonJsonErrorMessage(res.status));
      err.status = res.status;
      throw err;
    }
    if (!res.ok) {
      // Tenant-settings-scoping: 412 with error=configuration_required
      // carries {tab, card, field, provider, message} so the Admin UI can
      // deep-link the user straight to the missing integration input.
      // Broadcast an event that useConfigurationErrorToast picks up to
      // show a "Configure now" banner. Still throw so callers can handle
      // the failure in their own loading/error state.
      if (res.status === 412 && data?.error === "configuration_required" && data?.detail
          && !inlineConfigRequired) {
        window.dispatchEvent(
          new CustomEvent("configurationRequired", { detail: data.detail })
        );
      }
      const raw = data.error || data.detail || `HTTP ${res.status}`;
      const err = new Error(errorMessageFrom(raw));
      err.status = res.status;
      err.body = data;
      if (res.status === 412 && data?.error === "configuration_required") {
        err.configRequired = data.detail;
      }
      throw err;
    }
    return data;
  } catch (err) {
    // Suppress the error log for statuses the caller explicitly expects and
    // handles (keeps the console clean for "capability not applicable" reads).
    if (!(Array.isArray(quietStatuses) && err && quietStatuses.includes(err.status))) {
      log.error(`[${path}]`, err);
    }
    throw err;
  }
}

let _refreshInFlight = null;

export async function tryRefreshToken() {
  // Single-flight (A3-rotation): refresh tokens are now single-use on the server.
  // Without this, concurrent 401s each POST the SAME refresh token → the server
  // rotates once and rejects the rest → the user is spuriously logged out. Share
  // ONE in-flight refresh so every concurrent caller resolves against the same
  // new pair.
  if (_refreshInFlight) return _refreshInFlight;
  _refreshInFlight = _doRefresh().finally(() => { _refreshInFlight = null; });
  return _refreshInFlight;
}

async function _doRefresh() {
  // F (#16): the refresh token rides in the HttpOnly i2_refresh cookie; the
  // server rotates the cookie pair. No token touches JS. CSRF header required
  // (cookie-authed POST). Success is simply a 2xx — the new cookies are set
  // by the response's Set-Cookie.
  try {
    const res = await fetch(`${API_BASE}/api/auth/refresh`, {
      method: "POST",
      credentials: "include",
      headers: withCsrf({ "Content-Type": "application/json" }, "POST"),
    });
    if (!res.ok) return false;
    const data = await res.json();
    return !!data.success;
  } catch { return false; }
}

// ─── LLM API (BYOK-strict discovery) ─────────────────────

export const llmApi = {
  // GET /api/llm/providers-configured → { configured: [...], llm_ready: bool,
  //   local_endpoints: [{endpoint_id, display_name, provider_class, ...}] }
  providersConfigured: () => request("/api/llm/providers-configured"),
  // GET /api/llm/models → { <provider>: [{id, name}, ...], ... }.
  // Throws an Error with .status=412 if tenant has no provider configured.
  models: () => request("/api/llm/models"),
  // What "Workspace default" means right now (phase M) — for unsaved agents.
  workspaceDefault: () => request("/api/llm/workspace-default"),
};

// ─── BYOM — Local LLM Endpoints (Admin → Local LLM) ──────────────────────
// Per-tenant self-hosted LLM endpoints (Ollama / vLLM / LM Studio / TGI / NIM).
// All handlers return the backend {success, data} envelope; callers read .data.
// Admin-gated server-side (require_administrator).
export const localLlmApi = {
  // GET /api/llm/local-endpoints → { data: [endpoint, ...] } (incl. disabled)
  list: () => request("/api/llm/local-endpoints"),
  // POST /api/llm/local-endpoints → { data: endpoint }  (201)
  create: (body) =>
    request("/api/llm/local-endpoints", { method: "POST", body: JSON.stringify(body) }),
  // PUT /api/llm/local-endpoints/{id} → { data: endpoint }
  update: (endpointId, body) =>
    request(`/api/llm/local-endpoints/${encodeURIComponent(endpointId)}`, {
      method: "PUT", body: JSON.stringify(body),
    }),
  // DELETE /api/llm/local-endpoints/{id} → { data: { deleted } }.
  // 409 (with body.detail.rankings) when still referenced by a ranking.
  remove: (endpointId) =>
    request(`/api/llm/local-endpoints/${encodeURIComponent(endpointId)}`, { method: "DELETE" }),
  // POST /api/llm/local-endpoints/{id}/test → { data: { ok, latency_ms, models_count, embed_probe? } }
  test: (endpointId) =>
    request(`/api/llm/local-endpoints/${encodeURIComponent(endpointId)}/test`, { method: "POST" }),
  // POST /api/llm/local-endpoints/test-connection → ephemeral probe (no persist)
  testConnection: (body) =>
    request("/api/llm/local-endpoints/test-connection", {
      method: "POST", body: JSON.stringify(body),
    }),
  // GET /api/llm/local-endpoints/{id}/models → { data: { models, default_model, default_model_stale } }
  models: (endpointId) =>
    request(`/api/llm/local-endpoints/${encodeURIComponent(endpointId)}/models`),
  // GET /api/llm/local-provider-classes → { data: [{key, display_name, ...}] }
  providerClasses: () => request("/api/llm/local-provider-classes"),
  // GET /api/llm/non-local-purposes → { chat_is_local, non_local_purposes: [...] }.
  // Backs VerifyAllLocalBanner. The banner is informational + fail-silent, so if
  // the backend endpoint isn't present yet this 404s and the banner hides itself.
  nonLocalPurposes: () => request("/api/llm/non-local-purposes"),
};

// ─── Hosted Model Serving v1 — the platform model catalog + control plane ─────
// Backs Admin → Model Serving. Platform-admin-gated. The control-plane verbs
// (run/stop/remove) talk to the models GPU cluster; add kicks HF→S3 staging.
const _ms = (key) => `/api/model-serving/catalog/${encodeURIComponent(key)}`;
const _post = (path, body) =>
  request(path, { method: "POST", ...(body === undefined ? {} : { body: JSON.stringify(body) }) });

// Hosted Model Serving (Part II) — a tenant's self-service model lifecycle.
// Backs Admin → Model Serving. Every call is scoped to the caller's tenant.
export const modelServingApi = {
  // GET /catalog → every model: spec + revisions + serving status + requests_1h
  catalog: () => request("/api/model-serving/catalog"),
  // POST /lookup {repo, revision} → {model, recommendation, gpu_options, suggested_*}; 412 = needs a HF token
  lookup: (repo, revision = "main") =>
    request("/api/model-serving/lookup", {
      method: "POST", body: JSON.stringify({ repo, revision }), quietStatuses: [412, 404],
      inlineConfigRequired: true,
    }),
  // POST /catalog {repo, key, display_name, node_class, image, max_model_len, extra_args, ...} (201)
  add: (body) => _post("/api/model-serving/catalog", body),
  get: (key) => request(_ms(key)),
  // PATCH settings; restart=true applies them now (restarts a running model)
  saveSettings: (key, changes, restart = false) =>
    request(`${_ms(key)}${restart ? "?restart=true" : ""}`, { method: "PATCH", body: JSON.stringify(changes) }),
  remove: (key) => request(_ms(key), { method: "DELETE", quietStatuses: [409] }),
  warm: (key, nodeClass) => _post(`${_ms(key)}/warm`, nodeClass ? { node_class: nodeClass } : {}),
  stop: (key) => _post(`${_ms(key)}/stop`),
  status: (key) => request(`${_ms(key)}/status`),
  keepWarm: (key, keepWarm) => _post(`${_ms(key)}/keep-warm`, { keep_warm: !!keepWarm }),
  // GET /gpu-options?key= → every preset this cluster offers, with fit + reason + $/hr
  gpuOptions: (key) =>
    request(`/api/model-serving/gpu-options${key ? `?key=${encodeURIComponent(key)}` : ""}`),
  update: (key, revision = "main") => _post(`${_ms(key)}/update`, { revision }),
  switchVersion: (key, revisionId, policy = "now") =>
    _post(`${_ms(key)}/switch`, { revision_id: revisionId || null, policy }),
  cancelPendingSwitch: (key) => request(`${_ms(key)}/pending-switch`, { method: "DELETE" }),
  rollback: (key, policy = "now") => _post(`${_ms(key)}/rollback?policy=${encodeURIComponent(policy)}`),
  cancelDownload: (key, revisionId) =>
    _post(`${_ms(key)}/revisions/${encodeURIComponent(revisionId)}/cancel`),
  retryDownload: (key, revisionId) =>
    _post(`${_ms(key)}/revisions/${encodeURIComponent(revisionId)}/retry`),
  deleteVersion: (key, revisionId) =>
    request(`${_ms(key)}/revisions/${encodeURIComponent(revisionId)}`, { method: "DELETE" }),
  events: (key) => request(`${_ms(key)}/events`),
  usage: (key) => request(`${_ms(key)}/usage`),
  metrics: (key) => request(`${_ms(key)}/metrics`),
  logs: (key, tail = 200) => request(`${_ms(key)}/logs?tail=${tail}`),
  s3Models: () => request("/api/model-serving/s3-models"),
};

// ─── ML Platform — execution targets (Bar B: run ML in the customer account) ──
// Backs Admin → ML Execution Targets. Handlers return the target dict / list /
// {ok,...} directly (no {success,data} envelope). external_id is masked on read.
export const mlApi = {
  // GET /api/ml/execution-targets → [{id, connector_type, aws_account_id, ...}]
  listExecutionTargets: () => request("/api/ml/execution-targets"),
  // POST /api/ml/execution-targets → the created (masked) target
  createExecutionTarget: (body) =>
    request("/api/ml/execution-targets", { method: "POST", body: JSON.stringify(body) }),
  // GET /api/ml/execution-targets/{id}
  getExecutionTarget: (id) => request(`/api/ml/execution-targets/${id}`),
  // POST /api/ml/execution-targets/{id}/test → { ok, stage, message }
  testExecutionTarget: (id) =>
    request(`/api/ml/execution-targets/${id}/test`, { method: "POST" }),

  // ── Dataset specs (E4) ──────────────────────────────────────────────
  listDatasetSpecs: () => request("/api/ml/dataset-specs"),
  createDatasetSpec: (body) =>
    request("/api/ml/dataset-specs", { method: "POST", body: JSON.stringify(body) }),
  getDatasetSpec: (id) => request(`/api/ml/dataset-specs/${id}`),
  deleteDatasetSpec: (id) =>
    request(`/api/ml/dataset-specs/${id}`, { method: "DELETE", rawResponse: true }),

  // ── Jobs / runs (F4) ────────────────────────────────────────────────
  listJobs: () => request("/api/ml/jobs"),
  createJob: (body) => request("/api/ml/jobs", { method: "POST", body: JSON.stringify(body) }),
  getJob: (id) => request(`/api/ml/jobs/${id}`),
  // GET /api/ml/cost-estimate?instance_type=&instance_count=&max_runtime_seconds=&region=
  costEstimate: (params) => {
    const q = new URLSearchParams(params).toString();
    return request(`/api/ml/cost-estimate?${q}`);
  },

  // ── Models / results / promote (H4) ─────────────────────────────────
  listModels: (datasetSpecId) =>
    request(`/api/ml/models${datasetSpecId ? `?dataset_spec_id=${datasetSpecId}` : ""}`),
  getModel: (id) => request(`/api/ml/models/${id}`),
  getModelForecasts: (id, seriesKey) =>
    request(`/api/ml/models/${id}/forecasts${seriesKey ? `?series_key=${encodeURIComponent(seriesKey)}` : ""}`),
  getModelBacktests: (id) => request(`/api/ml/models/${id}/backtests`),
  // POST /api/ml/models/{id}/promote { to_status } → updated model  [administrator]
  promoteModel: (id, toStatus) =>
    request(`/api/ml/models/${id}/promote`, { method: "POST", body: JSON.stringify({ to_status: toStatus }) }),
};

// ─── Conversation Inspector API (Phase 1 sub-task E) ─────

// Single-call aggregator → full InspectorPayload from backend.
// Backed by Langfuse (spans/traces) + i² Postgres (identity/CSAT/verification).
export const conversationInspectorApi = {
  // GET /api/conversations/{id}/inspector → { conversation, customer, agent, turns, ... }
  get: (conversationId) =>
    request(`/api/conversations/${conversationId}/inspector`),

  // GET/POST /api/conversations/{id}/tags — B55: both now hit the
  // durable conversation_tags store (POST persists + Langfuse mirror).
  getTags: (conversationId) =>
    request(`/api/conversations/${conversationId}/tags`),
  addTag: (conversationId, tag) =>
    request(`/api/conversations/${conversationId}/tags`, {
      method: "POST",
      body: JSON.stringify({ tag }),
    }),

  // POST /api/conversations/{id}/flag
  flag: (conversationId, { assignee, queue = "default", notes } = {}) =>
    request(`/api/conversations/${conversationId}/flag`, {
      method: "POST",
      body: JSON.stringify({ assignee, queue, notes }),
    }),

  // POST /api/conversations/{id}/verify
  verify: (conversationId, verdict, notes) =>
    request(`/api/conversations/${conversationId}/verify`, {
      method: "POST",
      body: JSON.stringify({ verdict, notes }),
    }),

  // POST /api/conversations/{id}/replay  (sub-task G)
  replayTurn: (conversationId, traceId, observationId, editedSystemPrompt) =>
    request(`/api/conversations/${conversationId}/replay`, {
      method: "POST",
      body: JSON.stringify({
        trace_id: traceId,
        observation_id: observationId,
        edited_system_prompt: editedSystemPrompt,
      }),
    }),

  // GET /api/conversations/{id}/replay/prompt?observation_id=X
  // Returns the FULL original system prompt for an observation —
  // bypasses the 2000-char truncation the inspector aggregator does
  // on every span input. Used by ReplayTurnOverlay on open to
  // pre-populate the diff editor (Phase 1.5 #1 follow-up).
  getReplayOriginalPrompt: (conversationId, observationId) =>
    request(`/api/conversations/${conversationId}/replay/prompt?observation_id=${encodeURIComponent(observationId)}`),

  // Phase D · POST /api/inspector/diff — sibling-turn diff narrative
  diffTurns: (conversationId, leftTurnIndex, rightTurnIndex) =>
    request(`/api/inspector/diff`, {
      method: "POST",
      body: JSON.stringify({
        conversation_id: conversationId,
        left_turn_index: leftTurnIndex,
        right_turn_index: rightTurnIndex,
      }),
    }),

  // Phase D · GET /api/agents/{agent_id}/anomaly-clusters
  getAnomalyClusters: (agentId) =>
    request(`/api/agents/${agentId}/anomaly-clusters`),
};

// ─── Observability management surfaces (Phase 1 sub-task H) ──

// Build a `?limit=&offset=` query suffix, omitting either when not supplied.
// Returns "" when neither is given so un-paginated callers keep their bare URL.
const _pageQuery = (limit, offset) => {
  const p = new URLSearchParams();
  if (limit != null) p.set("limit", String(limit));
  if (offset != null) p.set("offset", String(offset));
  const s = p.toString();
  return s ? `?${s}` : "";
};

// All endpoints below are tenant-scoped backend-side via JWT.
export const observabilityApi = {
  // ─── Datasets ─────────────────────────────────────────
  listDatasets: () => request("/api/observability/datasets"),
  createDataset: (name, description) =>
    request("/api/observability/datasets", {
      method: "POST", body: JSON.stringify({ name, description }),
    }),
  addTraceToDataset: (datasetId, traceId, observationId, expectedOutput) =>
    request(`/api/observability/datasets/${datasetId}/items`, {
      method: "POST",
      body: JSON.stringify({
        trace_id: traceId,
        observation_id: observationId,
        expected_output: expectedOutput,
      }),
    }),
  listDatasetRuns: (datasetId, options = {}) =>
    request(`/api/observability/datasets/${datasetId}/runs`, options),
  // E3 · run-from-UI — start a scored eval run of `agentId` over a dataset.
  // Returns {success, data:{run_id, run_name, status}}. run_id = local UUID
  // (poll status by this); run_name = Langfuse run name (per-item drill-down).
  createDatasetRun: (datasetId, agentId, runName) =>
    request(`/api/observability/datasets/${datasetId}/runs`, {
      method: "POST",
      body: JSON.stringify({ agent_id: agentId, run_name: runName || undefined }),
    }),
  // E3 · live status of one run — poll while in-flight. `options` is spread
  // so callers can pass an AbortController { signal }. runId = local UUID.
  getDatasetRunStatus: (datasetId, runId, options = {}) =>
    request(
      `/api/observability/datasets/${datasetId}/runs/${encodeURIComponent(runId)}/status`,
      options,
    ),
  // E3 · recent runs for a dataset (newest first) — the "Recent runs" table.
  // `limit`/`offset` page the list; the rest of options (e.g. an abort signal)
  // is spread onto the request.
  listEvalRuns: (datasetId, { limit, offset, ...options } = {}) =>
    request(
      `/api/observability/datasets/${datasetId}/eval-runs${_pageQuery(limit, offset)}`,
      options,
    ),
  // B9 · per-item drill-down — scores for one run (runId = run name/key).
  // `limit`/`offset` page the run-items so only the requested page's per-item
  // trace scores are fetched (UI-4); the rest of options is spread.
  listDatasetRunItems: (datasetId, runId, { limit, offset, ...options } = {}) =>
    request(
      `/api/observability/datasets/${datasetId}/runs/${encodeURIComponent(runId)}/items${_pageQuery(limit, offset)}`,
      options,
    ),

  // ─── Golden-set items (D1) ────────────────────────────
  // List the dataset's golden Q&A items. `options` is spread so callers can
  // pass an AbortController { signal }.
  listDatasetItems: (datasetId, options = {}) =>
    request(`/api/observability/datasets/${datasetId}/items`, options),
  // Author a from-scratch item (question + optional expected answer).
  createDatasetItem: (datasetId, input, expectedOutput) =>
    request(`/api/observability/datasets/${datasetId}/items/author`, {
      method: "POST",
      body: JSON.stringify({ input, expected_output: expectedOutput }),
    }),
  // Bulk-import CSV/JSONL content → {created, failed, errors:[]}.
  importDatasetItems: (datasetId, content, filename) =>
    request(`/api/observability/datasets/${datasetId}/items/import`, {
      method: "POST",
      body: JSON.stringify({ content, filename }),
    }),
  // Inline edit — patch an item's input and/or expected answer.
  updateDatasetItem: (datasetId, itemId, patch) =>
    request(
      `/api/observability/datasets/${datasetId}/items/${encodeURIComponent(itemId)}`,
      { method: "PATCH", body: JSON.stringify(patch) },
    ),
  // Remove an item from the golden set.
  deleteDatasetItem: (datasetId, itemId) =>
    request(
      `/api/observability/datasets/${datasetId}/items/${encodeURIComponent(itemId)}`,
      { method: "DELETE" },
    ),

  // ─── Prompts ──────────────────────────────────────────
  listPrompts: () => request("/api/observability/prompts"),
  getPrompt: (name, label = "production") =>
    request(`/api/observability/prompts/${encodeURIComponent(name)}?label=${encodeURIComponent(label)}`),
  listPromptVersions: (name) =>
    request(`/api/observability/prompts/${encodeURIComponent(name)}/versions`),
  createPromptVersion: (name, content, labels = [], commitMessage = null) =>
    request(`/api/observability/prompts/${encodeURIComponent(name)}/versions`, {
      method: "POST",
      body: JSON.stringify({ content, labels, commit_message: commitMessage }),
    }),
  promotePrompt: (name, version, label = "production") =>
    request(`/api/observability/prompts/${encodeURIComponent(name)}/promote`, {
      method: "POST", body: JSON.stringify({ version, label }),
    }),

  // ─── Annotations ──────────────────────────────────────
  listAnnotations: (status) => {
    const qs = status ? `?status=${encodeURIComponent(status)}` : "";
    return request(`/api/observability/annotations${qs}`);
  },
  completeAnnotation: (annotationId, verdict, notes) =>
    request(`/api/observability/annotations/${annotationId}/complete`, {
      method: "POST", body: JSON.stringify({ verdict, notes }),
    }),

  // ─── Extended trace search ───────────────────────────
  searchTraces: (filters) => {
    const params = new URLSearchParams();
    if (filters?.q) params.set("q", filters.q);
    if (filters?.session_id) params.set("session_id", filters.session_id);
    if (filters?.user_id) params.set("user_id", filters.user_id);
    if (filters?.score_categorical)
      params.set("score_categorical", filters.score_categorical);
    if (filters?.min_latency_ms) params.set("min_latency_ms", filters.min_latency_ms);
    if (filters?.max_latency_ms) params.set("max_latency_ms", filters.max_latency_ms);
    if (filters?.tags) filters.tags.forEach((t) => params.append("tag", t));
    if (filters?.started_after) params.set("started_after", filters.started_after);
    if (filters?.started_before) params.set("started_before", filters.started_before);
    if (filters?.limit) params.set("limit", filters.limit);
    if (filters?.offset) params.set("offset", filters.offset);
    return request(`/api/observability/traces/search?${params.toString()}`);
  },

  // ─── V2.4/V2.2 self-observability + V2.1 voice latency ─
  getHealth: () => request("/api/observability/health"),
  getVoiceLatency: (agentId, range = "24h") =>
    request(
      `/api/observability/voice-latency?agent_id=${encodeURIComponent(agentId)}` +
      `&range=${encodeURIComponent(range)}`
    ),
};

// ─── Auth API ────────────────────────────────────────────

export const authApi = {
  register: async (tenantName, tenantSlug, name, email, password) => {
    const res = await request("/api/auth/register", {
      method: "POST",
      body: JSON.stringify({ tenant_name: tenantName, tenant_slug: tenantSlug, name, email, password }),
    }, false);
    if (res.success) storeAuth(res.data);
    return res;
  },

  login: async (email, password, tenantSlug, captchaToken) => {
    const body = { email, password, tenant_slug: tenantSlug };
    // A1-CAPTCHA: include the Turnstile token only when the user has solved the
    // challenge (shown after N failed attempts).
    if (captchaToken) body.captcha_token = captchaToken;
    const res = await request("/api/auth/login", {
      method: "POST",
      body: JSON.stringify(body),
    }, false);
    if (res.success) storeAuth(res.data);
    return res;
  },

  logout: () => {
    // Snappy UX: clear local profile immediately, then best-effort ask the
    // server to revoke the session (A3, #3) + clear the auth cookies. The
    // cookies are HttpOnly so only the server can delete them — fire the
    // cookie-authed POST (credentials + CSRF header) before we drop the CSRF
    // cookie locally. Fire-and-forget; the tokens are short-lived anyway.
    const csrf = getCsrfToken();
    fetch(`${API_BASE}/api/auth/logout`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json", ...(csrf ? { "X-CSRF-Token": csrf } : {}) },
    }).catch(() => { /* best-effort */ });
    clearAuth();
    window.dispatchEvent(new Event("auth:logout"));
  },

  me: () => request("/api/auth/me"),
  tenant: () => request("/api/auth/tenant"),
  users: () => request("/api/auth/users"),
  auditLogs: (limit = 50, severity = null) => {
    const params = new URLSearchParams({ limit });
    if (severity) params.set("severity", severity);
    return request(`/api/auth/audit-logs?${params}`);
  },
  getWhatsAppConfig: () => request("/api/auth/channel-config/whatsapp"),
  updateWhatsAppConfig: (data) =>
    request("/api/auth/channel-config/whatsapp", {
      method: "PUT",
      body: JSON.stringify(data),
    }),
  testWhatsAppConfig: () => request("/api/auth/channel-config/whatsapp/test", { method: "POST" }),
  sendTestWhatsAppMessage: (to, message) => request("/api/auth/channel-config/whatsapp/send-test", { method: "POST", body: JSON.stringify({ to, message }) }),
  getSMSConfig: () => request("/api/auth/channel-config/sms"),
  updateSMSConfig: (data) =>
    request("/api/auth/channel-config/sms", {
      method: "PUT",
      body: JSON.stringify(data),
    }),
  testSMSConfig: () => request("/api/auth/channel-config/sms/test", { method: "POST" }),
  getSmtpConfig: () => request("/api/auth/email-config"),
  updateSmtpConfig: (data) =>
    request("/api/auth/email-config", { method: "PUT", body: JSON.stringify(data) }),
  testSmtpConfig: () => request("/api/auth/email-config/test", { method: "POST" }),
  getFirecrawlConfig: () => request("/api/auth/firecrawl-config"),
  // Hugging Face token (Hosted Model Serving Part II) — only for gated/private repos
  getHuggingFaceConfig: () => request("/api/auth/huggingface-config"),
  updateHuggingFaceConfig: (data) =>
    request("/api/auth/huggingface-config", { method: "PUT", body: JSON.stringify(data) }),
  testHuggingFaceConfig: () => request("/api/auth/huggingface-config/test", { method: "POST" }),
  updateFirecrawlConfig: (data) =>
    request("/api/auth/firecrawl-config", { method: "PUT", body: JSON.stringify(data) }),
  testFirecrawlConfig: () => request("/api/auth/firecrawl-config/test", { method: "POST" }),
  getPlatformConfig: () => request("/api/auth/platform-config"),
  updatePlatformConfig: (data) =>
    request("/api/auth/platform-config", { method: "PUT", body: JSON.stringify(data) }),
  // CC-1 / Batch TS — tenant Guardrails & Safety floor.
  getGuardrailsConfig: () => request("/api/auth/guardrails-config"),
  updateGuardrailsConfig: (data) =>
    request("/api/auth/guardrails-config", { method: "PUT", body: JSON.stringify(data) }),
  // Cloud-fallback / air-gap toggle (LLM Rankings tab). When false, model_resolver
  // refuses cloud fallback — purposes resolve only to local endpoints.
  getAllowCloudFallback: () => request("/api/auth/allow-cloud-fallback"),
  setAllowCloudFallback: (allow_cloud_fallback) =>
    request("/api/auth/allow-cloud-fallback", { method: "PUT", body: JSON.stringify({ allow_cloud_fallback }) }),
  getGenesysConfig: () => request("/api/handoff/config"),
  updateGenesysConfig: (data) =>
    request("/api/handoff/genesys", { method: "PUT", body: JSON.stringify(data) }),
  setHandoffProvider: (provider) =>
    request("/api/handoff/provider", { method: "PUT", body: JSON.stringify({ provider }) }),
  testGenesysConfig: () => request("/api/handoff/test", { method: "POST" }),
  getOpenObserveConfig: () => request("/api/auth/openobserve-config"),
  getOpenObserveCredentials: () => request("/api/auth/openobserve-config/credentials"),
  provisionOpenObserve: () => request("/api/auth/openobserve-config/provision", { method: "POST" }),
  testOpenObserveConfig: () => request("/api/auth/openobserve-config/test", { method: "POST" }),
  getIntegrations: () => request("/api/auth/integrations"),
  invite: (email, name, role) =>
    request("/api/auth/invite", {
      method: "POST",
      body: JSON.stringify({ email, name, role }),
    }),
  updateUser: (userId, data) =>
    request(`/api/auth/users/${userId}`, { method: "PUT", body: JSON.stringify(data) }),
  deleteUser: (userId) =>
    request(`/api/auth/users/${userId}`, { method: "DELETE" }),

  acceptInvite: async (token, password) => {
    const res = await request("/api/auth/accept-invite", {
      method: "POST",
      body: JSON.stringify({ token, password }),
    }, false);
    if (res.success) storeAuth(res.data);
    return res;
  },
  forgotPassword: (email, tenantSlug) =>
    request("/api/auth/forgot-password", {
      method: "POST",
      body: JSON.stringify({ email, tenant_slug: tenantSlug }),
    }, false),
  resetPassword: (token, password) =>
    request("/api/auth/reset-password", {
      method: "POST",
      body: JSON.stringify({ token, password }),
    }, false),
  getLlmConfig: () => request("/api/auth/llm-config"),
  updateLlmConfig: (data) =>
    request("/api/auth/llm-config", {
      method: "PUT",
      body: JSON.stringify(data),
    }),
  testLlmConfig: () => request("/api/auth/llm-config/test", { method: "POST" }),
  getVertexConfig: () => request("/api/auth/llm-config/vertex"),
  updateVertexConfig: (data) =>
    request("/api/auth/llm-config/vertex", {
      method: "PUT",
      body: JSON.stringify(data),
    }),
  testVertexConfig: () => request("/api/auth/llm-config/vertex/test", { method: "POST" }),
  getAnthropicConfig: () => request("/api/auth/llm-config/anthropic"),
  updateAnthropicConfig: (data) =>
    request("/api/auth/llm-config/anthropic", {
      method: "PUT",
      body: JSON.stringify(data),
    }),
  testAnthropicConfig: () => request("/api/auth/llm-config/anthropic/test", { method: "POST" }),
  getBedrockConfig: () => request("/api/auth/llm-config/bedrock"),
  updateBedrockConfig: (data) =>
    request("/api/auth/llm-config/bedrock", {
      method: "PUT",
      body: JSON.stringify(data),
    }),
  testBedrockConfig: () => request("/api/auth/llm-config/bedrock/test", { method: "POST" }),
  getCohereConfig: () => request("/api/auth/llm-config/cohere"),
  updateCohereConfig: (data) =>
    request("/api/auth/llm-config/cohere", {
      method: "PUT",
      body: JSON.stringify(data),
    }),
  testCohereConfig: () => request("/api/auth/llm-config/cohere/test", { method: "POST" }),
  getVoyageConfig: () => request("/api/auth/llm-config/voyage"),
  updateVoyageConfig: (data) =>
    request("/api/auth/llm-config/voyage", {
      method: "PUT",
      body: JSON.stringify(data),
    }),
  testVoyageConfig: () => request("/api/auth/llm-config/voyage/test", { method: "POST" }),
  // Deepseek — first-class BYOK provider for deepseek-v4-flash + deepseek-v4-pro.
  // See docs/design/deepseek-provider-v1.md.
  getDeepseekConfig: () => request("/api/auth/llm-config/deepseek"),
  updateDeepseekConfig: (data) =>
    request("/api/auth/llm-config/deepseek", {
      method: "PUT",
      body: JSON.stringify(data),
    }),
  testDeepseekConfig: () => request("/api/auth/llm-config/deepseek/test", { method: "POST" }),
  // Track C.8 — embedding-model migration per agent
  migrateAgentEmbeddings: (agentId, targetProvider, targetModel) =>
    request(`/api/ingestion/agents/${agentId}/migrate-embeddings`, {
      method: "POST",
      body: JSON.stringify({ target_provider: targetProvider, target_model: targetModel }),
    }),
  getAgentEmbeddingMigration: (agentId) =>
    request(`/api/ingestion/agents/${agentId}/embedding-migration`),
  cancelAgentEmbeddingMigration: (agentId) =>
    request(`/api/ingestion/agents/${agentId}/embedding-migration/cancel`, { method: "POST" }),
  // LLM Models Discovery
  getModels: () => request("/api/auth/llm-models"),
  refreshModels: () => request("/api/auth/llm-models/refresh", { method: "POST" }),
  // LLM Rankings
  getLlmRankings: () => request("/api/auth/llm-rankings"),
  updateLlmRankings: (functionName, rankings) =>
    request("/api/auth/llm-rankings", {
      method: "PUT",
      body: JSON.stringify({ function: functionName, rankings }),
    }),
  deleteLlmRankings: (functionName) =>
    request(`/api/auth/llm-rankings/${functionName}`, { method: "DELETE" }),
};

// ─── Widget / API Keys ──────────────────────────────────

export const widgetApi = {
  listKeys: () => request("/api/widget/api-keys"),
  createKey: (name = "API Key", scopes = ["platform"]) =>
    request("/api/widget/api-keys", { method: "POST", body: JSON.stringify({ name, scopes }) }),
  revokeKey: (keyId) =>
    request(`/api/widget/api-keys/${keyId}/revoke`, { method: "PUT" }),
  deleteKey: (keyId) =>
    request(`/api/widget/api-keys/${keyId}`, { method: "DELETE" }),

  // Allowed-origins (per-agent CORS allowlist) — Admin → Channels → Chat
  // Widget → Allowed domains. `agentId` scopes to one agent; omit for the
  // workspace-wide (every-agent) rows. See widget-allowed-origins-v1.md.
  listAllowedOrigins: (agentId) =>
    request(`/api/widget/allowed-origins${agentId ? `?agent_id=${encodeURIComponent(agentId)}` : ""}`),
  addAllowedOrigin: (origin, agentId = null) =>
    request("/api/widget/allowed-origins", {
      method: "POST",
      body: JSON.stringify({ origin, agent_id: agentId }),
    }),
  deleteAllowedOrigin: (id) =>
    request(`/api/widget/allowed-origins/${id}`, { method: "DELETE" }),
  listBlockedOrigins: (agentId) =>
    request(`/api/widget/blocked-origins${agentId ? `?agent_id=${encodeURIComponent(agentId)}` : ""}`),
  allowBlockedOrigin: (blockedId) =>
    request("/api/widget/allowed-origins/allow-blocked", {
      method: "POST",
      body: JSON.stringify({ blocked_id: blockedId }),
    }),
};

// ─── Chat ────────────────────────────────────────────────

export const chatApi = {
  sendMessage: (userId, message, agentId = null, fileIds = null) =>
    request("/api/chat/message", {
      method: "POST",
      body: JSON.stringify({ user_id: userId, message, agent_id: agentId, file_ids: fileIds || undefined }),
    }),
  clearSession: (userId) =>
    request("/api/chat/clear", { method: "POST", body: JSON.stringify({ user_id: userId }) }),
  // Skills v2 CSAT-INFER: explicit star CSAT dropped — satisfaction is inferred.
  sendTestWhatsApp: (userId, message, agentId = null) =>
    request("/api/chat/test/whatsapp", {
      method: "POST",
      body: JSON.stringify({ user_id: userId, message, agent_id: agentId }),
    }),
  sendTestSMS: (userId, message, agentId = null) =>
    request("/api/chat/test/sms", {
      method: "POST",
      body: JSON.stringify({ user_id: userId, message, agent_id: agentId }),
    }),
};

// ─── Files ───────────────────────────────────────────────

export const filesApi = {
  // Chat-attachment upload. Routes through `request` so the 401→refresh
  // contract + auth header are handled by the client (ARC-013) instead of
  // being re-implemented at the call site. FormData body → browser sets the
  // multipart Content-Type.
  upload: (file, agentId = "") => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("agent_id", agentId || "");
    return request("/api/files/upload", { method: "POST", body: formData });
  },
};

// ─── Agents ──────────────────────────────────────────────

export const agentApi = {
  list: () => request("/api/agents"),
  // Server-side pagination + filter/sort/search for the Agents page.
  // Mirrors GET /api/agents' opt-in query params — omitting all of them
  // (i.e. calling list() above) is byte-identical to the pre-pagination
  // response, which every OTHER caller of list() still relies on.
  // Sentinel values ("all" for status/kind, "" for channel/category) are
  // passed straight through — the backend's own _norm_filter treats them
  // as "no filter", so this stays a thin pass-through with no duplicated
  // filter logic on the client.
  listPaged: ({
    page, pageSize, search, status, kind, channel, category, sort, includeExperts,
  } = {}) => {
    const qs = new URLSearchParams();
    if (page != null) qs.set("page", String(page));
    if (pageSize != null) qs.set("page_size", String(pageSize));
    if (search) qs.set("search", search);
    if (status) qs.set("status", status);
    if (kind) qs.set("kind", kind);
    if (channel != null) qs.set("channel", channel);
    if (category != null) qs.set("category", category);
    if (sort) qs.set("sort", sort);
    if (includeExperts != null) qs.set("include_experts", String(includeExperts));
    return request(`/api/agents?${qs.toString()}`);
  },
  get: (id) => request(`/api/agents/${id}`),
  // B51 (2026-07-23): version history / rollback / clone — the backend
  // endpoints predate this but were never wired into any UI.
  getVersions: (id) => request(`/api/agents/${id}/versions`),
  rollback: (id, versionNumber) =>
    request(`/api/agents/${id}/rollback/${versionNumber}`, { method: "POST" }),
  clone: (id) => request(`/api/agents/${id}/clone`, { method: "POST" }),
  // v13.x Supervisor RAG Routing v1 (Item 9): runtime capability +
  // auto-injected toolsets. Used by WiringTab to render the read-only
  // "Auto-injected" section.
  getCapability: (id) => request(`/api/agents/${id}/capability`),
  create: (data) => request("/api/agents", { method: "POST", body: JSON.stringify(data) }),
  // Phase A1 — pass `expectedTreeVersion` for stale-write detection.
  // Server returns HTTP 412 with {client_version, current_version,
  // current_updated_at} when the client's tree_version is stale.
  // The request() wrapper surfaces 412 as a thrown error carrying
  // .status === 412 and .body so the caller can render the conflict
  // banner via <AgentViewer> and refetch.
  update: (id, data, opts = {}) => {
    const qs = new URLSearchParams();
    if (opts.expectedTreeVersion != null) qs.set("expected_tree_version", String(opts.expectedTreeVersion));
    if (opts.expectedUpdatedAt) qs.set("expected_updated_at", opts.expectedUpdatedAt);
    const q = qs.toString();
    return request(`/api/agents/${id}${q ? `?${q}` : ""}`, { method: "PUT", body: JSON.stringify(data) });
  },
  delete: (id) => request(`/api/agents/${id}`, { method: "DELETE" }),
  // §19.14 / item H — generate a starter test script. Returns
  // {success, data: {filename, content_b64, format, case_count, ...}}.
  generateTestScript: (id, format, casesPerExpert = 8) => {
    const qs = new URLSearchParams({ format, cases_per_expert: String(casesPerExpert) });
    return request(`/api/agents/${id}/test-script?${qs.toString()}`, { method: "POST" });
  },
  // A3 — bindings (Wiring tab)
  bindMcp: (agentId, serverId) =>
    request(`/api/agents/${agentId}/bindings/mcp/${serverId}`, { method: "POST" }),
  unbindMcp: (agentId, serverId) =>
    request(`/api/agents/${agentId}/bindings/mcp/${serverId}`, { method: "DELETE" }),
  bindOpenapi: (agentId, integrationId) =>
    request(`/api/agents/${agentId}/bindings/openapi/${integrationId}`, { method: "POST" }),
  unbindOpenapi: (agentId, integrationId) =>
    request(`/api/agents/${agentId}/bindings/openapi/${integrationId}`, { method: "DELETE" }),
  bindChannel: (agentId, channelType) =>
    request(`/api/agents/${agentId}/bindings/channel/${channelType}`, { method: "POST" }),
  unbindChannel: (agentId, channelType) =>
    request(`/api/agents/${agentId}/bindings/channel/${channelType}`, { method: "DELETE" }),
  bindWorkflow: (agentId, workflowId) =>
    request(`/api/agents/${agentId}/bindings/workflow/${workflowId}`, { method: "POST" }),
  unbindWorkflow: (agentId, workflowId) =>
    request(`/api/agents/${agentId}/bindings/workflow/${workflowId}`, { method: "DELETE" }),
  // A4 — test binding health
  testBinding: (agentId, bindingId) =>
    request(`/api/agents/${agentId}/bindings/test/${bindingId}`, { method: "POST" }),
  getDataSources: (id) => request(`/api/agents/${id}/data-sources`),
  // Detail Viewer (Phase B per agent-detail-viewer-v1.md §10)
  getTree: (id) => request(`/api/agents/${id}/tree`),
  getGraph: (id) => request(`/api/agents/${id}/graph`),
  getActivity: (id, params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/api/agents/${id}/activity${qs ? `?${qs}` : ""}`);
  },
  getWiring: (id) => request(`/api/agents/${id}/wiring`),
  // Agent Model Transparency v1 (RB-2) — per-purpose LLM resolution for the
  // Models card. Read-only; runs the SAME resolver the runtime uses.
  modelResolution: (id) => request(`/api/agents/${id}/model-resolution`),
  // C2 — atomic Builder save
  putTree: (id, payload) =>
    request(`/api/agents/${id}/tree`, { method: "PUT", body: JSON.stringify(payload) }),
  // ASC-v2: atomically CREATE an agent + apply its canvas tree in one txn.
  // Body: { agent: <AgentCreate>, experts, integrations, workflows }.
  createTree: (payload) =>
    request(`/api/agents/tree`, { method: "POST", body: JSON.stringify(payload) }),
  // C20-C22 — export / import / publish-tree
  exportTree: (id) =>
    request(`/api/agents/${id}/export`, { method: "POST" }),
  importTree: (payload) =>
    request(`/api/agents/import`, { method: "POST", body: JSON.stringify(payload) }),
  publishTree: (payload) =>
    request(`/api/templates/publish-tree`, { method: "POST", body: JSON.stringify(payload) }),
  // Per-agent Knowledge (B12)
  getKnowledgeLibrary: (id, params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/api/agents/${id}/knowledge/library${qs ? `?${qs}` : ""}`);
  },
  getKnowledgeJobs: (id, params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/api/agents/${id}/knowledge/jobs${qs ? `?${qs}` : ""}`);
  },
  getKnowledgeSettings: (id) => request(`/api/agents/${id}/knowledge/settings`),
  updateKnowledgeSettings: (id, payload) =>
    request(`/api/agents/${id}/knowledge/settings`, { method: "PUT", body: JSON.stringify(payload) }),
  // Kill-switch (read = any authenticated user; mutate = admin)
  killSwitchStatus: () => request("/api/supervisor/killswitch/status"),
  killSwitchTrip: (reason) => request("/api/supervisor/killswitch/trip", { method: "POST", body: JSON.stringify({ reason }) }),
  killSwitchReset: () => request("/api/supervisor/killswitch/reset", { method: "POST" }),
  updateStatus: (id, status) => request(`/api/agents/${id}/status`, { method: "PUT", body: JSON.stringify({ status }) }),
  // ASC-v3 RH-STORE: persist an expert's routing examples to the field the runtime
  // reads (exemplars_json) with regenerated embeddings — NOT metadata_json.exemplars.
  updateExemplars: (id, examples) =>
    request(`/api/agents/${id}/exemplars`, { method: "PUT", body: JSON.stringify({ examples }) }),
  // Margin-routing config (per-supervisor floor/margin) + the live test-utterance
  // preview. Both write/read flow_config.supervisor.routing the runtime resolves.
  updateRoutingConfig: (id, { floor, margin }) =>
    request(`/api/agents/${id}/routing-config`, { method: "PUT", body: JSON.stringify({ floor, margin }) }),
  previewRouting: (id, body) =>
    request(`/api/agents/${id}/routing-preview`, { method: "POST", body: JSON.stringify(body) }),
  // Self-hosted model serving status (Red/Amber/Green): green=ready, amber=warming
  // (cold start), red=off/unavailable. Powers the ModelServingLight indicator.
  modelServingStatus: (endpointId) =>
    request(`/api/llm/local-endpoints/${endpointId}/serving-status`),
  // Serving status for the on-demand model an AGENT's chat is bound to. Returns
  // {on_demand:false} when the agent isn't on an on-demand model (light hides).
  agentServingStatus: (agentId) =>
    request(`/api/llm/agents/${agentId}/serving-status`),
  bootstrapSuperAgent: () => request("/api/agent-builder-tools/bootstrap", { method: "POST" }),
  bootstrapPlatformTester: () => request("/api/agent-builder-tools/bootstrap-tester", { method: "POST" }),
  bootstrapOrchestrator: () => request("/api/agent-builder-tools/bootstrap-orchestrator", { method: "POST" }),
  // Learning / Fine-tuning
  getFewShots: (id, count = 5, days = 30) => request(`/api/agents/${id}/learn/few-shots?count=${count}&days=${days}`),
  applyFewShots: (id) => request(`/api/agents/${id}/learn/few-shots/apply`, { method: "POST" }),
  analyzeAgent: (id, days = 30) => request(`/api/agents/${id}/learn/analyze?days=${days}`),
  applySuggestion: (id, suggestion) => request(`/api/agents/${id}/learn/apply-suggestion`, { method: "POST", body: JSON.stringify(suggestion) }),
  getAccuracyStats: (id, days = 30) => request(`/api/agents/${id}/learn/accuracy?days=${days}`),
  getLearningSuggestions: (id, days = 7) => request(`/api/agents/${id}/learn/suggestions?days=${days}`),
  backfillRewards: (id, days = 7) => request(`/api/agents/${id}/learn/backfill-rewards?days=${days}`, { method: "POST" }),
  // Fine-tuning
  exportTrainingData: (id) => request(`/api/agents/${id}/fine-tune/export`, { method: "POST" }),
  // BYOK-strict: caller must pass provider + base_model explicitly; backend
  // will 412 if the tenant has no LLM configured.
  startFineTuning: (id, provider, model) =>
    request(`/api/agents/${id}/fine-tune/start`, { method: "POST", body: JSON.stringify({ provider, base_model: model }) }),
  getFineTuneStatus: (id, runId) => request(`/api/agents/${id}/fine-tune/status/${runId}`),
  evaluateGroundTruth: (id) => request(`/api/agents/${id}/fine-tune/evaluate`, { method: "POST" }),
  deployFineTuned: (id, runId, abTest = true) =>
    request(`/api/agents/${id}/fine-tune/deploy/${runId}`, { method: "POST", body: JSON.stringify({ enable_ab_test: abTest }) }),
  getFineTuneHistory: (id) => request(`/api/agents/${id}/fine-tune/history`),
  addGroundTruth: (id, question, expectedAnswer, sourceRef = null) =>
    request(`/api/agents/${id}/fine-tune/ground-truth`, { method: "POST", body: JSON.stringify({ question, expected_answer: expectedAnswer, source_reference: sourceRef }) }),
  listGroundTruth: (id) => request(`/api/agents/${id}/fine-tune/ground-truth`),
  getReviewQueue: (id, limit = 20) => request(`/api/agents/${id}/fine-tune/review-queue?limit=${limit}`),
  reviewResponse: (id, logId, approved, note = null) =>
    request(`/api/agents/${id}/fine-tune/review/${logId}`, { method: "POST", body: JSON.stringify({ approved, note }) }),
};

// ─── Auto-tune (v13 Phase E) ────────────────────────────
//
// Backed by `POST /api/agents/{id}/infer-schema` in `app/routers/agents.py`.
// `force=true` persists the inferred shape directly to
// `agents.template_overlay`; default behavior returns the inferred schema
// for the wizard UI to edit before save.
export const autoTuneApi = {
  inferSchema: (agentId, { sampleText, force = false } = {}) =>
    request(`/api/agents/${agentId}/infer-schema`, {
      method: "POST",
      body: JSON.stringify({
        ...(sampleText ? { sample_text: sampleText } : {}),
        ...(force ? { force: true } : {}),
      }),
    }),
};

// ─── v13 domain-template catalog ─────────────────────────
//
// Read-only listing of the 12 phase-1 knowledge-extraction templates
// (Network/Telecom, Healthcare, IT Helpdesk, Code, Legal, Finance,
// Supply Chain, HR, Project Management, Sales/CRM, Engineering/DevOps
// + generic). The Knowledge Schema panel uses `list()` to populate
// its template-picker dropdown so an admin can manually bind an agent
// to a curated template (instead of running the auto-tune wizard).

export const domainTemplateApi = {
  list: () => request("/api/domain-templates"),
  get: (id) => request(`/api/domain-templates/${encodeURIComponent(id)}`),
};

// ─── Workflows ──────────────────────────────────────────

export const workflowApi = {
  list: () => request("/api/workflows"),
  get: (id) => request(`/api/workflows/${id}`),
  create: (data) => request("/api/workflows", { method: "POST", body: JSON.stringify(data) }),
  update: (id, data) => request(`/api/workflows/${id}`, { method: "PUT", body: JSON.stringify(data) }),
  delete: (id) => request(`/api/workflows/${id}`, { method: "DELETE" }),
  updateStatus: (id, status) => request(`/api/workflows/${id}/status`, { method: "PATCH", body: JSON.stringify({ status }) }),
  validate: (id) => request(`/api/workflows/${id}/validate`, { method: "POST" }),
  executions: (id, limit = 20) => request(`/api/workflows/${id}/executions?limit=${limit}`),
};

// ─── Skills v1 M6 (2026-07-01) — base skills + instances + CL review ────

export const skillsApi = {
  // Base skills
  listBase: () => request("/api/skills/base"),
  getBase: (name) => request(`/api/skills/base/${encodeURIComponent(name)}`),
  createBase: (payload) => request("/api/skills/base", { method: "POST", body: JSON.stringify(payload) }),
  // Hard-delete a base skill this workspace owns. `cascade` also detaches the
  // caller's own instances first (the API refuses outright when another
  // workspace has it attached, or when the skill ships on disk).
  deleteBase: (name, { cascade = false } = {}) =>
    request(
      `/api/skills/base/${encodeURIComponent(name)}${cascade ? "?cascade=true" : ""}`,
      { method: "DELETE", quietStatuses: [403, 409] },
    ),

  // Instances
  listInstances: () => request("/api/skills/instances"),
  getInstance: (id) => request(`/api/skills/instances/${id}`),
  exportInstance: (id) => request(`/api/skills/instances/${id}/export`, { rawResponse: true }),
  importInstance: (payload) => request("/api/skills/instances/import", { method: "POST", body: JSON.stringify(payload) }),
  cloneInstance: (id, targetAgentId) => request(`/api/skills/instances/${id}/clone`, { method: "POST", body: JSON.stringify({ target_agent_id: targetAgentId }) }),
  copyToTenant: (id, targetTenantId, targetAgentId) => request(`/api/skills/instances/${id}/copy-to-tenant`, {
    method: "POST",
    body: JSON.stringify({ target_tenant_id: targetTenantId, target_agent_id: targetAgentId }),
  }),

  // Discover + attach
  discover: (agentId) => request(`/api/agents/${agentId}/skills/discover`, { method: "POST" }),
  attach: (agentId, skillName, tableName) => request(`/api/agents/${agentId}/skills/attach`, {
    method: "POST",
    body: JSON.stringify({ skill_name: skillName, table_name: tableName }),
  }),
  detach: (agentId, instanceId) => request(`/api/agents/${agentId}/skills/${instanceId}`, { method: "DELETE" }),

  // Auto-attach opt-out toggle (B, skills-gap-closure)
  getAutoAttach: (agentId) => request(`/api/agents/${agentId}/skills/auto-attach`),
  setAutoAttach: (agentId, enabled) => request(`/api/agents/${agentId}/skills/auto-attach`, {
    method: "PUT",
    body: JSON.stringify({ enabled }),
  }),

  // Pending examples (CL review)
  listPending: (instanceId) => request(`/api/skills/instances/${instanceId}/pending-examples`),
  applyPending: (instanceId, exId) => request(`/api/skills/instances/${instanceId}/pending-examples/${exId}/apply`, { method: "POST" }),
  rejectPending: (instanceId, exId, reason = "") => request(`/api/skills/instances/${instanceId}/pending-examples/${exId}/reject`, {
    method: "POST",
    body: JSON.stringify({ reason }),
  }),

  // Observability (M11 item 70) — powers the Observability tab in SkillsView
  getObservability: (since = "7d") => request(`/api/skills/observability?since=${encodeURIComponent(since)}`),
  rebaseInstance: (id) => request(`/api/skills/instances/${id}/rebase`, { method: "POST" }),

  // Skills v2 (2026-08-19) — on-the-agent UX (SkillsSection in the Agent
  // Overview tab). suggestions() drives the Active/Suggested split;
  // capabilities() feeds the "Uses:" chip row + Needs·Connect deep-links;
  // previewImpact() runs the eval-engine before/after on a dataset.
  suggestions: (agentId) => request(`/api/agents/${agentId}/skills/suggestions`),
  quality: (agentId) => request(`/api/agents/${agentId}/skills/quality`),
  // A2/A4 — one aggregated payload (quality + per-skill + proposals + air-gap
  // consolidation availability) for the Quality & Improve surface.
  qualitySummary: (agentId) => request(`/api/agents/${agentId}/skills/quality-summary`),
  // L2 — the unified closed loop: improvement proposals for an agent.
  improvements: (agentId, status) =>
    request(`/api/agents/${agentId}/improvements${status ? `?status=${encodeURIComponent(status)}` : ""}`),
  getProposal: (proposalId) =>
    request(`/api/skills/proposals/${encodeURIComponent(proposalId)}`),
  applyProposal: (proposalId) =>
    request(`/api/skills/proposals/${encodeURIComponent(proposalId)}/apply`, { method: "POST" }),
  revertProposal: (proposalId) =>
    request(`/api/skills/proposals/${encodeURIComponent(proposalId)}/revert`, { method: "POST" }),
  // L2/L3 manual triggers (normally cron-driven) — run one improvement cycle,
  // re-measure standing lift (A/B flywheel), or plan air-gap consolidation.
  runImprovement: (agentId, body) =>
    request(`/api/agents/${agentId}/improvements/run`, { method: "POST", body: JSON.stringify(body) }),
  runFlywheel: (agentId, body) =>
    request(`/api/agents/${agentId}/skills/flywheel/run`, { method: "POST", body: JSON.stringify(body) }),
  consolidatePlan: (agentId) =>
    // 403 (non-admin) is an expected "not available here" read — keep it quiet.
    // (412 no longer used — the endpoint now returns 200 {available, reason}.)
    request(`/api/agents/${agentId}/skills/consolidate/plan`, { quietStatuses: [403] }),
  consolidateRun: (agentId) =>
    request(`/api/agents/${agentId}/skills/consolidate/run`, { method: "POST", quietStatuses: [403] }),
  // Tenant consent switch for air-gap in-boundary weight consolidation. Admin-only
  // (403 for non-admins is surfaced so the UI can explain who can change it).
  consolidateOptIn: (agentId, enabled) =>
    request(`/api/agents/${agentId}/skills/consolidate/opt-in`, {
      method: "POST", body: JSON.stringify({ enabled }),
    }),
  // Hosted-provider skills (companion to pushSkillHosted).
  listHosted: () => request(`/api/skills/hosted`),

  // Skills v2 library (S-LIB, 2026-08-19) — Gallery browse + rate, Your skills,
  // and the Author flow. Gallery/yourSkills return bare arrays; components
  // unwrap defensively. publishSkill surfaces 422 (eval-gate) / 409 / 403.
  gallery: (category) => request(`/api/skills/gallery${category ? `?category=${encodeURIComponent(category)}` : ""}`),
  publishSkill: (name, payload) => request(`/api/skills/base/${encodeURIComponent(name)}/publish`, { method: "POST", body: JSON.stringify(payload) }),
  rateSkill: (name, payload) => request(`/api/skills/base/${encodeURIComponent(name)}/rate`, { method: "POST", body: JSON.stringify(payload) }),
  skillRatings: (name) => request(`/api/skills/base/${encodeURIComponent(name)}/ratings`),
  yourSkills: () => request(`/api/skills/your-skills`),

  // Open SKILL.md portability (Workstream H). Export → raw zip (download via
  // fetch+blob, per the no-naive-anchor-to-authed-API rule). Import → draft.
  exportSkill: (name) => request(`/api/skills/base/${encodeURIComponent(name)}/export`, { rawResponse: true }),
  importSkillFile: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return request(`/api/skills/import/file`, { method: "POST", body: fd });
  },
  importSkillUrl: (url) => request(`/api/skills/import/url`, { method: "POST", body: JSON.stringify({ url }) }),
  importSkillGit: (ref) => request(`/api/skills/import/git`, { method: "POST", body: JSON.stringify({ ref }) }),
  // H7 — outbound push to a hosted provider (content can't be pulled back).
  pushSkillHosted: (name, provider = "anthropic") =>
    request(`/api/skills/base/${encodeURIComponent(name)}/push-hosted`, {
      method: "POST", body: JSON.stringify({ provider }),
    }),

  // Executable-skills sandbox (status / consent toggle / scripts / runs).
  sandboxStatus: () => request(`/api/skills/sandbox/status`),
  sandboxEnable: (enabled) => request(`/api/skills/sandbox/enable`, {
    method: "PUT", body: JSON.stringify({ enabled }),
  }),
  // Set the tenant's executable-skills RESOURCE caps (wall-clock/cpu/mem/scratch).
  // The server clamps to platform bounds; the isolation invariants aren't settable.
  sandboxLimits: (caps) => request(`/api/skills/sandbox/limits`, {
    method: "PUT", body: JSON.stringify(caps),
  }),
  sandboxScripts: () => request(`/api/skills/sandbox/scripts`),
  sandboxRuns: () => request(`/api/skills/sandbox/runs`),
  // Download a run's generated artifact. Authed endpoint → must go through
  // request() (JWT header) + blob, NEVER a naive <a href> (which sends no auth).
  downloadArtifact: async (urlPath, filename) => {
    const res = await request(urlPath, { rawResponse: true });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || "artifact";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },
  // Executable-skill review (owner/admin): static-check findings + record a
  // decision. Approval pins the current script bytes.
  skillStaticCheck: (name) =>
    request(`/api/skills/base/${encodeURIComponent(name)}/static-check`),
  skillReview: (name, decision, notes) =>
    request(`/api/skills/base/${encodeURIComponent(name)}/review`, {
      method: "POST", body: JSON.stringify({ decision, notes }),
    }),

  authorDraft: (sentence) => request(`/api/skills/author/draft`, { method: "POST", body: JSON.stringify({ sentence }) }),
  authorSave: (bodyMd) => request(`/api/skills/author/save`, { method: "POST", body: JSON.stringify({ body_md: bodyMd }) }),
  // Lint a SKILL.md draft (no save) → { valid, issues:[{severity,field,line,message}] }.
  authorValidate: (bodyMd) => request(`/api/skills/author/validate`, { method: "POST", body: JSON.stringify({ body_md: bodyMd }) }),
  capabilities: (agentId, skillName) =>
    request(`/api/agents/${agentId}/skills/${encodeURIComponent(skillName)}/capabilities`),
  previewImpact: (agentId, skillName, datasetId, sampleSize = 5) =>
    request(`/api/agents/${agentId}/skills/${encodeURIComponent(skillName)}/preview-impact`, {
      method: "POST",
      body: JSON.stringify({ dataset_id: datasetId, sample_size: sampleSize }),
    }),
  // Measure a DRAFT (unpublished, possibly edited) skill body BEFORE saving —
  // the "measure" step of the edit → measure → publish loop. Runs the paired
  // with/without eval on a golden set, injecting the draft body transiently.
  previewCandidateImpact: (agentId, { datasetId, bodyMd, sampleSize = 5 }) =>
    request(`/api/agents/${agentId}/skills/preview-candidate-impact`, {
      method: "POST",
      body: JSON.stringify({ dataset_id: datasetId, body_md: bodyMd, sample_size: sampleSize }),
    }),
  // Attach a GENERAL (non-data) skill — no table required (brand voice, etc.).
  attachGeneral: (agentId, skillName) =>
    request(`/api/agents/${agentId}/skills/${encodeURIComponent(skillName)}/attach-general`, {
      method: "POST",
    }),
};


// ─── Templates / Marketplace ─────────────────────────────

export const templateApi = {
  list: (params = {}) => {
    const qs = new URLSearchParams(params);
    return request(`/api/templates?${qs}`);
  },
  marketplace: (params = {}) => {
    const qs = new URLSearchParams(params);
    return request(`/api/templates/marketplace?${qs}`);
  },
  categories: () => request("/api/templates/categories"),
  get: (id) => request(`/api/templates/${id}`),
  create: (data) => request("/api/templates", { method: "POST", body: JSON.stringify(data) }),
  update: (id, data) => request(`/api/templates/${id}`, { method: "PUT", body: JSON.stringify(data) }),
  delete: (id) => request(`/api/templates/${id}`, { method: "DELETE" }),
  install: (id, data = {}) => request(`/api/templates/${id}/install`, { method: "POST", body: JSON.stringify(data) }),
  publishFromAgent: (agentId, data) => request(`/api/templates/publish-from-agent/${agentId}`, { method: "POST", body: JSON.stringify(data) }),
  rate: (id, data) => request(`/api/templates/${id}/rate`, { method: "POST", body: JSON.stringify(data) }),
  ratings: (id, params = {}) => {
    const qs = new URLSearchParams(params);
    return request(`/api/templates/${id}/ratings?${qs}`);
  },
};

// ─── Webhooks ───────────────────────────────────────────

export const webhookApi = {
  list: () => request("/api/webhooks"),
  get: (id) => request(`/api/webhooks/${id}`),
  create: (data) => request("/api/webhooks", { method: "POST", body: JSON.stringify(data) }),
  update: (id, data) => request(`/api/webhooks/${id}`, { method: "PUT", body: JSON.stringify(data) }),
  delete: (id) => request(`/api/webhooks/${id}`, { method: "DELETE" }),
  updateStatus: (id, status) => request(`/api/webhooks/${id}/status`, { method: "PATCH", body: JSON.stringify({ status }) }),
  regenerateSecret: (id) => request(`/api/webhooks/${id}/regenerate-secret`, { method: "POST" }),
  events: (id, limit = 50, offset = 0) => request(`/api/webhooks/${id}/events?limit=${limit}&offset=${offset}`),
  test: (id) => request(`/api/webhooks/${id}/test`, { method: "POST" }),
};

// ─── Knowledge Base ──────────────────────────────────────

export const knowledgeApi = {
  search: (agentId, query, topK = 5, scoreThreshold = null) =>
    request("/api/knowledge/search", { method: "POST", body: JSON.stringify({ agent_id: agentId, query, top_k: topK, ...(scoreThreshold != null && { score_threshold: scoreThreshold }) }) }),
  deleteDocument: (agentId, docId) =>
    request("/api/knowledge/document", { method: "DELETE", body: JSON.stringify({ agent_id: agentId, doc_id: docId }) }),
  stats: (agentId) => request(`/api/knowledge/stats/${agentId}`),
};

// ─── Ingestion Pipeline ─────────────────────────────────

export const ingestionApi = {
  crawl: (agentId, url, options = {}) =>
    request("/api/ingestion/crawl", {
      method: "POST",
      body: JSON.stringify({ agent_id: agentId, url, ...options }),
    }),
  uploadPipeline: async (agentId, files, options = {}) => {
    const formData = new FormData();
    formData.append("agent_id", agentId);
    files.forEach((f) => formData.append("files", f));
    if (options.chunk_size != null) formData.append("chunk_size", options.chunk_size);
    if (options.chunk_overlap != null) formData.append("chunk_overlap", options.chunk_overlap);
    if (options.metadata_tags) formData.append("metadata_tags", JSON.stringify(options.metadata_tags));
    const res = await fetch(`${API_BASE}/api/ingestion/upload`, { method: "POST", credentials: "include", headers: withCsrf({}, "POST"), body: formData });
    const data = await res.json();
    if (!res.ok) {
      const raw = data.error || data.detail || `HTTP ${res.status}`;
      const err = new Error(errorMessageFrom(raw));
      err.status = res.status;
      err.body = data;
      throw err;
    }
    return data;
  },
  ingestText: (agentId, text, options = {}) =>
    request("/api/ingestion/text", {
      method: "POST",
      body: JSON.stringify({ agent_id: agentId, text, ...options }),
    }),
  listJobs: (agentId = null, status = null) => {
    const params = new URLSearchParams();
    if (agentId) params.set("agent_id", agentId);
    if (status) params.set("status", status);
    return request(`/api/ingestion/jobs?${params}`);
  },
  bulkUpload: async (agentId, zipFile, options = {}) => {
    const formData = new FormData();
    formData.append("agent_id", agentId);
    formData.append("file", zipFile);
    if (options.chunk_size != null) formData.append("chunk_size", options.chunk_size);
    if (options.chunk_overlap != null) formData.append("chunk_overlap", options.chunk_overlap);
    if (options.parallel_workers != null) formData.append("parallel_workers", options.parallel_workers);
    const res = await fetch(`${API_BASE}/api/ingestion/bulk-upload`, { method: "POST", credentials: "include", headers: withCsrf({}, "POST"), body: formData });
    const data = await res.json();
    if (!res.ok) {
      const raw = data.error || data.detail || `HTTP ${res.status}`;
      const err = new Error(errorMessageFrom(raw));
      err.status = res.status;
      err.body = data;
      throw err;
    }
    return data;
  },
  getJob: (jobId) => request(`/api/ingestion/jobs/${jobId}`),
  cancelJob: (jobId) => request(`/api/ingestion/jobs/${jobId}/cancel`, { method: "POST" }),
  deleteJob: (jobId) => request(`/api/ingestion/jobs/${jobId}`, { method: "DELETE" }),
  clearJobs: () => request("/api/ingestion/jobs", { method: "DELETE" }),
  listSources: (agentId, includeDeleted = false) => {
    const params = new URLSearchParams({ agent_id: agentId });
    if (includeDeleted) params.set("include_deleted", "true");
    return request(`/api/ingestion/sources?${params}`);
  },

  // ─── Track A.5 / Batch 6: 7-store cascade delete + restore ──────────
  // Soft delete = 30-day grace; promotes to hard via the daily cron.
  // Hard delete = immediate cascade across vectors + metadata + graph
  //   nodes + structured tables + MinIO + ES + cache invalidation.
  // Restore = undo soft-delete within the grace window.
  softDeleteDocument: (docId, reason, agentId) =>
    request(`/api/ingestion/documents/${docId}?mode=soft${reason ? `&reason=${encodeURIComponent(reason)}` : ""}${agentId ? `&agent_id=${encodeURIComponent(agentId)}` : ""}`,
      { method: "DELETE" }),
  hardDeleteDocument: (docId, reason, agentId) =>
    request(`/api/ingestion/documents/${docId}?mode=hard${reason ? `&reason=${encodeURIComponent(reason)}` : ""}${agentId ? `&agent_id=${encodeURIComponent(agentId)}` : ""}`,
      { method: "DELETE" }),
  restoreDocument: (docId, agentId) =>
    request(`/api/ingestion/documents/${docId}/restore${agentId ? `?agent_id=${encodeURIComponent(agentId)}` : ""}`, { method: "POST" }),
  bulkDeleteDocuments: (docIds, mode = "soft", reason = null, agentId = null) =>
    request("/api/ingestion/documents/bulk-delete", {
      method: "POST",
      body: JSON.stringify({ doc_ids: docIds, mode, reason, agent_id: agentId }),
    }),
  bulkRestoreDocuments: (docIds, agentId = null) =>
    request("/api/ingestion/documents/bulk-restore", {
      method: "POST",
      body: JSON.stringify({ doc_ids: docIds, agent_id: agentId }),
    }),
  reEnrichDocument: (docId, stages, agentId) =>
    request(`/api/ingestion/documents/${docId}/re-enrich${agentId ? `?agent_id=${encodeURIComponent(agentId)}` : ""}`, {
      method: "POST",
      body: JSON.stringify({ stages }),
    }),
  // Track A.6 / Batch 6.5-6.7: per-agent bulk re-enrich + status polling.
  // Backend reuses the worker pool — no new pods. ``only_failed`` defaults
  // true (re-run only docs with a failed/skipped stage); set false after
  // a model swap to refresh every doc's enrichment.
  reEnrichAllAgent: (agentId, { stages, only_failed = true, cost_acknowledged = false } = {}) =>
    request(`/api/ingestion/agents/${agentId}/re-enrich-all`, {
      method: "POST",
      body: JSON.stringify({ stages, only_failed, cost_acknowledged }),
    }),
  getReEnrichStatus: (agentId) =>
    request(`/api/ingestion/agents/${agentId}/re-enrich-status`),
  // Track D.17 / Batch 9: deterministic English + structured summary
  // of what was actually extracted from a completed job.
  getJobSummary: (jobId) => request(`/api/ingestion/jobs/${jobId}/summary`),

  /**
   * Chunked upload for large files (>100MB). Splits file into 10MB chunks,
   * uploads each chunk, then triggers assembly + ingestion.
   * @param {string} agentId
   * @param {File} file - browser File object
   * @param {object} options - {chunk_size, chunk_overlap, classifications, onProgress}
   * @returns {Promise<{job_id, status}>}
   */
  chunkedUpload: async (agentId, file, options = {}) => {
    const CHUNK_SIZE = 10 * 1024 * 1024; // 10MB per chunk
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    // F (#16): cookie auth + CSRF header on every chunk POST.
    const headers = withCsrf({}, "POST");
    const fetchOpts = { method: "POST", credentials: "include", headers };

    // 1. Init session
    const initForm = new FormData();
    initForm.append("filename", file.name);
    initForm.append("agent_id", agentId);
    initForm.append("total_size", file.size);
    initForm.append("total_chunks", totalChunks);
    if (options.chunk_size != null) initForm.append("chunk_size", options.chunk_size);
    if (options.chunk_overlap != null) initForm.append("chunk_overlap", options.chunk_overlap);
    if (options.classifications) initForm.append("classifications", options.classifications);

    const initRes = await fetch(`${API_BASE}/api/ingestion/upload/init`, { ...fetchOpts, body: initForm });
    const initData = await initRes.json();
    if (!initRes.ok) throw new Error(initData.detail || `Init failed: HTTP ${initRes.status}`);
    const uploadId = initData.data.upload_id;

    // 2. Upload chunks
    for (let i = 0; i < totalChunks; i++) {
      const start = i * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, file.size);
      const blob = file.slice(start, end);

      const chunkForm = new FormData();
      chunkForm.append("upload_id", uploadId);
      chunkForm.append("chunk_index", i);
      chunkForm.append("chunk", blob, `chunk_${i}`);

      const chunkRes = await fetch(`${API_BASE}/api/ingestion/upload/chunk`, { ...fetchOpts, body: chunkForm });
      if (!chunkRes.ok) {
        const err = await chunkRes.json();
        throw new Error(err.detail || `Chunk ${i} failed: HTTP ${chunkRes.status}`);
      }

      if (options.onProgress) options.onProgress({ chunk: i + 1, total: totalChunks, percent: Math.round(((i + 1) / totalChunks) * 100) });
    }

    // 3. Complete — assemble and ingest
    const completeForm = new FormData();
    completeForm.append("upload_id", uploadId);
    const completeRes = await fetch(`${API_BASE}/api/ingestion/upload/complete`, { ...fetchOpts, body: completeForm });
    const completeData = await completeRes.json();
    if (!completeRes.ok) throw new Error(completeData.detail || `Complete failed: HTTP ${completeRes.status}`);

    return completeData;
  },
};

// ─── MCP / Bifrost ───────────────────────────────────────

// Random v4-ish id for the X-Mcp-Idempotency-Key header.
const _newIdemKey = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID)
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

const _withIdem = (opts = {}) => ({
  ...opts,
  headers: { ...(opts.headers || {}), "X-Mcp-Idempotency-Key": _newIdemKey() },
});

export const mcpApi = {
  // ─── Existing endpoints ─────────────────────────────
  listServers: () => request("/api/mcp/servers"),
  getServer: (serverId) => request(`/api/mcp/servers/${serverId}`),
  registerServer: (data) =>
    request("/api/mcp/servers", _withIdem({ method: "POST", body: JSON.stringify(data) })),
  unregisterServer: (serverId) => request(`/api/mcp/servers/${serverId}`, { method: "DELETE" }),
  reconcile: () => request("/api/mcp/servers/reconcile", { method: "POST" }),
  discoverTools: (serverId) => request(`/api/mcp/servers/${serverId}/tools`),
  allTools: () => request("/api/mcp/tools"),
  callTool: (serverId, toolName, args = {}) =>
    request("/api/mcp/tools/call", { method: "POST", body: JSON.stringify({ server_id: serverId, tool_name: toolName, arguments: args }) }),
  health: () => request("/api/mcp/health"),
  serverHealth: (serverId) => request(`/api/mcp/servers/${serverId}/health`),

  // ─── Milestone F — tenant MCP infrastructure ────────
  status: () => request("/api/mcp/status"),
  provision: () => request("/api/mcp/provision", _withIdem({ method: "POST" })),
  triggerReconcile: () => request("/api/mcp/status/reconcile", { method: "POST" }),
  // Server-side reconciler pass — hits the new /api/mcp/reconcile endpoint
  // added in the MCP remediation v1 PR. Returns counts + error list.
  // See design mcp-remediation-v1.md §13.15.
  reconcile: ({ un_quarantine = false } = {}) =>
    request("/api/mcp/reconcile", {
      method: "POST",
      body: JSON.stringify({ un_quarantine }),
    }),
  patchSpec: (patch) => request("/api/mcp/spec", _withIdem({ method: "PATCH", body: JSON.stringify(patch) })),

  // ─── Milestone F — per-server actions ──────────────
  editServer: (serverId, patch) =>
    request(`/api/mcp/servers/${serverId}`, _withIdem({ method: "PUT", body: JSON.stringify(patch) })),
  retryRegistration: (serverId) =>
    request(`/api/mcp/servers/${serverId}/retry`, _withIdem({ method: "POST" })),
  resetCircuit: (serverId) =>
    request(`/api/mcp/servers/${serverId}/reset-circuit`, _withIdem({ method: "POST" })),
  testServer: (data) =>
    request("/api/mcp/servers/test", _withIdem({ method: "POST", body: JSON.stringify(data) })),

  // ─── Milestone F — observability ───────────────────
  serverLogs: (serverId, lines = 200) =>
    request(`/api/mcp/servers/${serverId}/logs?lines=${lines}`),
  serverMetrics: (serverId, window = "24h") =>
    request(`/api/mcp/servers/${serverId}/metrics?window=${window}`),

  // ─── Milestone F — tenant allowlist ────────────────
  getAllowlist: () => request("/api/mcp/allowlist"),
  putAllowlist: (hosts) =>
    request("/api/mcp/allowlist", _withIdem({ method: "PUT", body: JSON.stringify({ hosts }) })),
};

// ─── Milestone E — credential vault ────────────────────

export const credentialsApi = {
  listProviders: () => request("/api/mcp/credential-providers"),
  // MCP-UI-1 — runtime options + common STDIO server presets (Postgres, etc.)
  stdioPresets: () => request("/api/mcp/stdio-presets"),
  list: () => request("/api/mcp/credentials"),
  get: (id) => request(`/api/mcp/credentials/${id}`),
  getDependents: (id) => request(`/api/mcp/credentials/${id}/servers`),
  create: (data) =>
    request("/api/mcp/credentials", _withIdem({ method: "POST", body: JSON.stringify(data) })),
  update: (id, data) =>
    request(`/api/mcp/credentials/${id}`, _withIdem({ method: "PUT", body: JSON.stringify(data) })),
  delete: (id, force = false) =>
    request(`/api/mcp/credentials/${id}${force ? "?force=true" : ""}`, { method: "DELETE" }),
  // Phase E1 — admin-triggered OAuth refresh round-trip. Only valid on
  // credential_type='oauth_managed' rows.
  testOauthRefresh: (id) =>
    request(`/api/mcp/credentials/${id}/test-oauth-refresh`, _withIdem({ method: "POST" })),
};

// ─── OpenAPI Integrations (universal REST connector) ───

export const openapiApi = {
  list: () => request("/api/openapi-integrations"),
  get: (id) => request(`/api/openapi-integrations/${id}`),
  getSpec: (id) => request(`/api/openapi-integrations/${id}/spec`),
  previewSpec: (data) =>
    request("/api/openapi-integrations/preview-spec", { method: "POST", body: JSON.stringify(data) }),
  create: (data) =>
    request("/api/openapi-integrations", { method: "POST", body: JSON.stringify(data) }),
  update: (id, data) =>
    request(`/api/openapi-integrations/${id}`, { method: "PUT", body: JSON.stringify(data) }),
  delete: (id) => request(`/api/openapi-integrations/${id}`, { method: "DELETE" }),
  testCredential: (id) =>
    request(`/api/openapi-integrations/${id}/test-credential`, { method: "POST" }),
  test: (id) =>
    request(`/api/openapi-integrations/${id}/test`, { method: "POST" }),
  // Wizard v2 — probe an in-progress configuration before persisting.
  // Same payload shape as create() but includes credential_value
  // inline; server stages the credential ephemerally for the probe.
  testConfig: (data) =>
    request("/api/openapi-integrations/test-config", { method: "POST", body: JSON.stringify(data) }),
  // Wizard v2 — acquire a spec from any URL (OpenAPI 3.x, Swagger 2.0,
  // or HTML docs page). Auto-detects the format. Same service backs
  // the Super Agent's obtain_openapi_spec tool.
  obtainSpec: ({ source_url, hint }) =>
    request("/api/openapi-integrations/obtain-spec", {
      method: "POST",
      body: JSON.stringify({ source_url, hint: hint || null }),
    }),
  refresh: (id) =>
    request(`/api/openapi-integrations/${id}/refresh`, { method: "POST" }),
  // Mid-scope refresh (Tier-2 #6): preview returns the diff WITHOUT
  // committing. Admin reviews + clicks Accept → refreshApply commits
  // the spec the preview cached server-side under preview_token.
  refreshPreview: (id) =>
    request(`/api/openapi-integrations/${id}/refresh-preview`, { method: "POST" }),
  refreshApply: (id, preview_token) =>
    request(`/api/openapi-integrations/${id}/refresh-apply`, {
      method: "POST",
      body: JSON.stringify({ preview_token }),
    }),
  // OAuth2 authorization-code (RFC 6749 §4.1 + RFC 7636 PKCE S256).
  // /start mints state + verifier server-side and returns the provider's
  // authorize URL with PKCE challenge; the wizard navigates the browser
  // there. /callback is provider-driven (not called from the SPA) — it
  // 302s back to /admin/openapi-integrations/<id>?oauth=connected.
  oauthStart: (id) =>
    request(`/api/openapi-integrations/${id}/oauth/start`),
};

// ─── Voice ──────────────────────────────────────────────

export const voiceApi = {
  getConfig: () => request("/api/voice/config"),
  updateConfig: (data) =>
    request("/api/voice/config", {
      method: "PUT",
      body: JSON.stringify(data),
    }),
  testConfig: () => request("/api/voice/config/test", { method: "POST" }),
  joinSession: (agentId = null) =>
    request("/api/voice/join", {
      method: "POST",
      body: JSON.stringify({ agent_id: agentId || undefined }),
    }),
  leaveSession: (roomName) =>
    request(`/api/voice/leave?room_name=${encodeURIComponent(roomName)}`, {
      method: "POST",
    }),
};

// ─── Analytics ───────────────────────────────────────────

export const analyticsApi = {
  stats: () => request("/api/analytics/stats"),
  health: () => request("/api/health"),
  graphMermaid: () => request("/api/graph/mermaid"),
  dashboard: (days = 7) => request(`/api/analytics/dashboard?days=${days}`),
  esSearch: (index, query, filters = null, size = 20) =>
    request("/api/analytics/search", { method: "POST", body: JSON.stringify({ index, query, filters, size }) }),
  esSummarize: (query = "", filters = null, size = 50) =>
    request("/api/analytics/summarize", { method: "POST", body: JSON.stringify({ index: "messages", query, filters, size }) }),
  esStats: () => request("/api/analytics/es-stats"),
  ask: (question, context = null) =>
    request("/api/analytics/ask", { method: "POST", body: JSON.stringify({ question, context }) }),
};

// ─── Dataplex ────────────────────────────────────────────

export const dataplexApi = {
  getConfig: () => request("/api/dataplex/config"),
  updateConfig: (data) =>
    request("/api/dataplex/config", { method: "PUT", body: JSON.stringify(data) }),
  discover: (query = "type=TABLE", location = "global") =>
    request("/api/dataplex/discover", { method: "POST", body: JSON.stringify({ query, location }) }),
  sync: ({ data_store_id, location = "global" } = {}) =>
    request("/api/dataplex/sync", { method: "POST", body: JSON.stringify({ location, data_store_id }) }),
  getAssets: (query = "type=TABLE", location = "global") =>
    request(`/api/dataplex/assets?query=${encodeURIComponent(query)}&location=${encodeURIComponent(location)}`),
  listDataStores: () => request("/api/dataplex/data-stores"),
  getStatus: () => request("/api/dataplex/status"),
  getSemanticDefinitions: () => request("/api/dataplex/semantic-definitions"),
  updateSemanticDefinitions: (overrides) =>
    request("/api/dataplex/semantic-definitions", { method: "PUT", body: JSON.stringify({ overrides }) }),
  getGroundingReport: () => request("/api/dataplex/grounding-report"),
};

// ─── Supervisor ─────────────────────────────────────────

export const supervisorApi = {
  dashboard: (days = null) => request(`/api/supervisor/dashboard${days ? `?days=${days}` : ""}`),
  conversations: (status = null, agentId = null, channel = null, limit = 20, offset = 0, days = null, q = null, escalated = null) => {
    // Phase 1.5 #3 extended params:
    //   - channel now multi-select (array or string both accepted)
    //   - q full-text search on message content
    //   - escalated boolean filter
    const params = new URLSearchParams();
    if (status) {
      (Array.isArray(status) ? status : [status]).forEach(s => params.append("status", s));
    }
    if (agentId) params.set("agent_id", agentId);
    if (channel) {
      (Array.isArray(channel) ? channel : [channel]).forEach(c => params.append("channel", c));
    }
    if (days) params.set("days", days);
    if (q) params.set("q", q);
    if (escalated !== null && escalated !== undefined) {
      params.set("escalated", escalated ? "true" : "false");
    }
    params.set("limit", limit);
    params.set("offset", offset);
    return request(`/api/supervisor/conversations?${params}`);
  },
  // Returns the raw Response so the caller can read a CSV blob. Routes
  // through `request` so the 401→refresh contract + auth header are handled
  // by the client (ARC-013), not re-implemented at the call site.
  exportConversations: (days = 30) =>
    request(`/api/supervisor/conversations/export?days=${days}`, { rawResponse: true }),
  conversationDetail: (id) => request(`/api/supervisor/conversations/${id}`),
  resolveConversation: (id, summary = null) =>
    request(`/api/supervisor/conversations/${id}/resolve`, {
      method: "POST",
      body: JSON.stringify({ summary }),
    }),
  agentPerformance: (days = 7) => request(`/api/supervisor/agents/performance?days=${days}`),
  // Tags
  getTags: (conversationId) => request(`/api/supervisor/conversations/${conversationId}/tags`),
  addTag: (conversationId, tag) => request(`/api/supervisor/conversations/${conversationId}/tags`, { method: "POST", body: JSON.stringify({ tag }) }),
  removeTag: (conversationId, tag) => request(`/api/supervisor/conversations/${conversationId}/tags/${encodeURIComponent(tag)}`, { method: "DELETE" }),
  tagSuggestions: (prefix = "") => request(`/api/supervisor/tags/suggestions?prefix=${encodeURIComponent(prefix)}`),
  tagAnalytics: (days = 30) => request(`/api/supervisor/tags/analytics?days=${days}`),
};

// ─── Campaigns ──────────────────────────────────────────

export const campaignApi = {
  list: (status, channel) => {
    const params = new URLSearchParams();
    if (status) params.set("status", status);
    if (channel) params.set("channel", channel);
    return request(`/api/campaigns?${params}`);
  },
  get: (id) => request(`/api/campaigns/${id}`),
  create: (data) => request("/api/campaigns", { method: "POST", body: JSON.stringify(data) }),
  update: (id, data) => request(`/api/campaigns/${id}`, { method: "PUT", body: JSON.stringify(data) }),
  delete: (id) => request(`/api/campaigns/${id}`, { method: "DELETE" }),
  trigger: (id) => request(`/api/campaigns/${id}/trigger`, { method: "POST" }),
  pause: (id) => request(`/api/campaigns/${id}/pause`, { method: "POST" }),
  reset: (id) => request(`/api/campaigns/${id}/reset`, { method: "POST" }),
  messages: (id, status) => {
    const params = new URLSearchParams();
    if (status) params.set("status", status);
    return request(`/api/campaigns/${id}/messages?${params}`);
  },

  // ─── Outbound voice v3 (D-phase) ────────────────────────
  resume: (id) =>
    request(`/api/campaigns/${id}/resume`, { method: "POST" }),
  // D6 — B16-shipped hard-stop endpoint. Differs from pause:
  //   pause → status=paused, unsent rows stay pending, resumable
  //   stop  → status=completed[_with_errors], unsent rows cancelled
  //           with failed_reason=campaign_stopped, NOT resumable
  // The dispatcher already supports this; the Stop button just needs
  // a dedicated client method to hit it.
  stop: (id) =>
    request(`/api/campaigns/${id}/stop`, { method: "POST" }),
  dispositions: (id, limit = 50) =>
    request(`/api/campaigns/${id}/dispositions?limit=${limit}`),
  recording: (campaignId, messageId, ttlSeconds = 600) =>
    request(
      `/api/campaigns/${campaignId}/messages/${messageId}/recording?ttl_seconds=${ttlSeconds}`
    ),
  validateRecipients: (payload) =>
    request("/api/campaigns/recipients/validate", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  clone: (id) =>
    // Just fetches the campaign — the create modal uses the result to
    // pre-fill its form (excluding recipients + name). D12 is a UI
    // wrapper around this + campaignApi.create; no separate backend
    // endpoint needed.
    request(`/api/campaigns/${id}`),
};

// ─── Outbound voice v3 — admin + dashboard endpoints ──────

export const outboundVoiceApi = {
  // D3 attestation modal
  attestationStatus: () =>
    request("/api/admin/outbound-voice/attestation"),
  attest: (attestationTextSha256) =>
    request("/api/admin/outbound-voice/attest", {
      method: "POST",
      body: JSON.stringify({ attestation_text_sha256: attestationTextSha256 }),
    }),

  // D7 tenant-scoped voice outbound settings (10 managed fields)
  getSettings: () => request("/api/admin/outbound-voice/settings"),
  // G-BE — single-shot snapshot for the OutboundVoiceConsole.
  // Returns {tenant_settings, attestation, trunks, agents, campaigns,
  // integration_status, drift, audit_tail}. The console renders the
  // sticky-left-nav status + 5 sections + audit panel from this one
  // call instead of fanning out to 5 separate endpoints.
  consoleSnapshot: () => request("/api/admin/outbound-voice/console"),
  updateSettings: (patch) =>
    request("/api/admin/outbound-voice/settings", {
      method: "PUT",
      body: JSON.stringify(patch),
    }),

  // D9 active calls panel
  activeCalls: (limit = 100) =>
    request(`/api/voice/active-calls?limit=${limit}`),

  // D2 voice trunks admin tab
  listTrunks: () => request("/api/voice/trunks"),
  createTrunk: (data) =>
    request("/api/voice/trunks", { method: "POST", body: JSON.stringify(data) }),
  getTrunk: (id) => request(`/api/voice/trunks/${id}`),
  updateTrunk: (id, data) =>
    request(`/api/voice/trunks/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    }),
  testTrunk: (id, wet = false) =>
    request(`/api/voice/trunks/${id}/test?wet=${wet ? "true" : "false"}`, {
      method: "POST",
    }),
  deleteTrunk: (id) =>
    request(`/api/voice/trunks/${id}`, { method: "DELETE" }),

  // Multi-tenant voice inbound v1 (2026-07-17) — Admin > Voice > Phone Numbers
  listPhoneNumbers: (includeInactive = true) =>
    request(`/api/voice/phone-numbers?include_inactive=${includeInactive ? "true" : "false"}`),
  createPhoneNumber: (data) =>
    request("/api/voice/phone-numbers", { method: "POST", body: JSON.stringify(data) }),
  getPhoneNumber: (id) => request(`/api/voice/phone-numbers/${id}`),
  updatePhoneNumber: (id, data) =>
    request(`/api/voice/phone-numbers/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),
  deletePhoneNumber: (id) =>
    request(`/api/voice/phone-numbers/${id}`, { method: "DELETE" }),
  verifyPhoneNumber: (id) =>
    request(`/api/voice/phone-numbers/${id}/verify`, { method: "POST" }),

  // D10 inbound→outbound cross-link
  recentOutboundCalls: (profileId, lookbackHours = 72, limit = 5) =>
    request(
      `/api/identity/profiles/${profileId}/recent-outbound-calls?lookback_hours=${lookbackHours}&limit=${limit}`
    ),
};

// ─── Identity & Customer Memory (D-24b) ─────────────────
// Supervisor-scoped. The Customer Memory admin surface (search a customer →
// review + delete what an agent has learned about them) uses these.
export const identityApi = {
  searchProfiles: (q, limit = 50) =>
    request(`/api/identity/profiles/search?q=${encodeURIComponent(q)}&limit=${limit}`),
  getMemories: (profileId, agentId) =>
    request(`/api/identity/profiles/${profileId}/memories${agentId ? `?agent_id=${encodeURIComponent(agentId)}` : ""}`),
  deleteMemory: (memoryId) =>
    request(`/api/identity/memories/${memoryId}`, { method: "DELETE" }),
  deleteAllMemories: (profileId) =>
    request(`/api/identity/profiles/${profileId}/memories`, { method: "DELETE" }),
  memoryStats: () => request("/api/identity/memories/stats"),
};

// ─── A/B Tests ──────────────────────────────────────────

export const abTestApi = {
  list: (status) => request(`/api/ab-tests${status ? `?status=${status}` : ""}`),
  get: (id) => request(`/api/ab-tests/${id}`),
  create: (data) => request("/api/ab-tests", { method: "POST", body: JSON.stringify(data) }),
  update: (id, data) => request(`/api/ab-tests/${id}`, { method: "PUT", body: JSON.stringify(data) }),
  delete: (id) => request(`/api/ab-tests/${id}`, { method: "DELETE" }),
  start: (id) => request(`/api/ab-tests/${id}/start`, { method: "POST" }),
  stop: (id) => request(`/api/ab-tests/${id}/stop`, { method: "POST" }),
};

// ─── Compliance ─────────────────────────────────────────

export const complianceApi = {
  exportLogs: async (format = "json", since, until, action, severity) => {
    const params = new URLSearchParams({ format });
    if (since) params.set("since", since);
    if (until) params.set("until", until);
    if (action) params.set("action", action);
    if (severity) params.set("severity", severity);
    // CSV path returns text/csv; bypass the JSON-parsing request() helper
    // and return the body as a string so the caller can turn it into a Blob.
    if (format === "csv") {
      const res = await fetch(`${API_BASE}/api/compliance/audit-logs/export?${params}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.text();
    }
    return request(`/api/compliance/audit-logs/export?${params}`);
  },
  retentionStats: () => request("/api/compliance/retention/stats"),
  applyRetention: (retention_days, dry_run = true) =>
    request("/api/compliance/retention/apply", { method: "POST", body: JSON.stringify({ retention_days, dry_run }) }),
  soc2Report: (days = 90) => request(`/api/compliance/reports/soc2?days=${days}`),
  gdprReport: () => request("/api/compliance/reports/gdpr"),
  dsarReport: (profileId) => request(`/api/compliance/reports/dsar/${profileId}`),
};

// ─── Eval Harness ──────────────────────────────────────
//
// ─── Billing ────────────────────────────────────────────

export const billingApi = {
  usage: () => request("/api/billing/usage"),
  checkQuota: (resource) => request(`/api/billing/check/${resource}`),
  updatePlan: (plan) => request("/api/billing/plan", { method: "PUT", body: JSON.stringify({ plan }) }),
  updateQuotas: (data) => request("/api/billing/quotas", { method: "PUT", body: JSON.stringify(data) }),
  plans: () => request("/api/billing/plans"),
};

// ─── Schedules ──────────────────────────────────────────

export const scheduleApi = {
  list: (status) => request(`/api/schedules${status ? `?status=${status}` : ""}`),
  get: (id) => request(`/api/schedules/${id}`),
  create: (data) => request("/api/schedules", { method: "POST", body: JSON.stringify(data) }),
  update: (id, data) => request(`/api/schedules/${id}`, { method: "PUT", body: JSON.stringify(data) }),
  delete: (id) => request(`/api/schedules/${id}`, { method: "DELETE" }),
  pause: (id) => request(`/api/schedules/${id}/pause`, { method: "POST" }),
  resume: (id) => request(`/api/schedules/${id}/resume`, { method: "POST" }),
  runNow: (id) => request(`/api/schedules/${id}/run-now`, { method: "POST" }),
  presets: () => request("/api/schedules/presets"),
};

// ─── Approvals ──────────────────────────────────────────

export const approvalApi = {
  list: (status = "pending") => request(`/api/approvals?status=${status}`),
  stats: () => request("/api/approvals/stats"),
  approve: (id, note = null) => request(`/api/approvals/${id}/approve`, { method: "POST", body: JSON.stringify({ note }) }),
  reject: (id, note = null) => request(`/api/approvals/${id}/reject`, { method: "POST", body: JSON.stringify({ note }) }),
};

// ─── Voice Recording & QA ───────────────────────────────

export const voiceQaApi = {
  listCalls: (qaStatus, limit = 20) => request(`/api/voice/calls?${qaStatus ? `qa_status=${qaStatus}&` : ""}limit=${limit}`),
  getTranscript: (id) => request(`/api/voice/calls/${id}/transcript`),
  getAudioUrl: (id) => request(`/api/voice/calls/${id}/audio-url`),
  /** Fetch the audio bytes through the API (JWT-authed) and return a
   *  browser-local `blob:` URL. Lifetime: caller is responsible for
   *  `URL.revokeObjectURL(url)` when done. Used by Supervisor > Voice
   *  QA's Play Audio button — we can't hand the browser a MinIO
   *  presigned URL because its SigV4 signature is bound to the
   *  cluster-internal MinIO hostname. */
  async fetchAudioBlobUrl(id) {
    const res = await fetch(`${API_BASE}/api/voice/calls/${id}/audio-stream`, { credentials: "include" });
    if (!res.ok) {
      const err = new Error(`HTTP ${res.status}: failed to fetch audio`);
      err.status = res.status;
      throw err;
    }
    const blob = await res.blob();
    return URL.createObjectURL(blob);
  },
  submitQa: (id, score, notes) => request(`/api/voice/calls/${id}/qa?score=${score}${notes ? `&notes=${encodeURIComponent(notes)}` : ""}`, { method: "POST" }),
};

// ─── Tenant Branding (logo) ─────────────────────────────

export const brandingApi = {
  uploadLogo: async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    const res = await fetch(`${API_BASE}/api/branding/logo`, { method: "POST", credentials: "include", headers: withCsrf({}, "POST"), body: formData });
    const data = await res.json();
    if (!res.ok) {
      const raw = data.error || data.detail || `HTTP ${res.status}`;
      const err = new Error(errorMessageFrom(raw));
      err.status = res.status;
      err.body = data;
      throw err;
    }
    return data;
  },
  removeLogo: () => request("/api/branding/logo", { method: "DELETE" }),
  /** Fetch the tenant logo bytes through the API (JWT-authed) and return
   *  a browser-local `blob:` URL — a bare <img src> can't attach the
   *  bearer token. Caller owns the lifetime (URL.revokeObjectURL when
   *  done). Mirrors voiceQaApi.fetchAudioBlobUrl. */
  async fetchLogoBlobUrl() {
    const res = await fetch(`${API_BASE}/api/branding/logo`, { credentials: "include" });
    if (!res.ok) {
      const err = new Error(`HTTP ${res.status}: failed to fetch logo`);
      err.status = res.status;
      throw err;
    }
    const blob = await res.blob();
    return URL.createObjectURL(blob);
  },
};
